import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Progress,
  Radio,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
  notification,
} from "antd";
import type { RadioChangeEvent, SelectProps, UploadFile } from "antd";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import {
  CAMPAIGN_TEMPLATES_OPTIONS,
  CAMPAIGN_TYPE_OPTIONS,
  ChannelKeyMap,
  describeDefaultIncompleteProfileFields,
  INCOMPLETE_PROFILE_FIELD_OPTIONS,
  IncompleteProfileFieldOption,
  RfmPeriod,
  TEMPLATE_TYPE_DESCRIPTIONS,
} from "../constants/labels";
import {
  CampaignTypeEnum,
  ChannelEnum,
  EndScheduleEnum,
  ModeEnum,
  RFMPeriod,
  StatusEnum,
  TemplatesTypeEnum,
  TypeEnum,
  WeekDay,
} from "../constants/enums";
import { brandMemberSegmentService } from "../services/brandMemberSegmentService";
import { useBrandStore } from "../store/brandStore";
import { brandRFMProfileService } from "../services/brandRFMProfileService";
import {
  CheckCircleFilled,
  CloseOutlined,
  DeleteFilled,
  DeleteOutlined,
  DownOutlined,
  ExclamationCircleFilled,
  EyeOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  PlusOutlined,
  SearchOutlined,
  SyncOutlined,
  UpOutlined,
} from "@ant-design/icons";
import { useWatch } from "antd/es/form/Form";
import { VARS, PROFILE_COMPLETION_REMINDER_VARS } from "../constants/variables";
import { RoutePaths } from "../routes/route";
import { UploadChangeParam } from "antd/es/upload";
import { Image as AntdImage } from "antd";
import DOMPurify from "dompurify";
import {
  getCampaignEVoucherSkus,
  htmlToWhatsappText,
  mapToEVouchers,
  whatsappTextToHtml,
} from "../utils/convert";
import {
  ALL_BRAND,
  mapCampaignToFormValues,
} from "../utils/campaignFormMapper";
import VoucherTable from "../components/common/VoucherTable";
import {
  CampaignDetails,
  EstimatedTotalMembersRequest,
  ExcludeMember,
  TargetMember,
} from "../models/campaign";
import { campaignService } from "../services/campaignService";
import { billingService } from "../services/billingService";
import { PostalDistricts } from "../models/postal";
import { useQueryNavigator } from "../utils/navigate";
import { DateRange } from "../models/time";
import {
  BlockRange,
  buildDisabledTime,
  parseBlockedRanges,
} from "../utils/date";
import { Voucher } from "../models/voucher";
import { EVoucher } from "../models/evoucher";
import { CampaignFormValues } from "../models/formValues";
import { helperService } from "../services/helperService";
import RFMGeneratingModal from "../components/common/GeneratingDataModal";
import CustomModal from "../components/common/Modal";
import EditorQuill from "../components/common/EditorQuill";
import EmailTemplateEditor, {
  parseEmailTemplate,
  flattenEditableContent,
  quillHtmlToInner,
  resolveQuillAlignments,
  reconstructEditableContent,
  ParsedEmailTemplate,
} from "../components/common/EmailTemplateEditor";
import { getChannelCost } from "../constants/cost";
import { getSmsCharCount, getSmsSegments, isGsmText } from "../constants/text";
import { MessageTemplateData } from "../models/messageTemplates";
import MessageTemplatesModal from "../components/common/MessageTemplatesModal";
import PreviewMemberModal from "../components/common/PreviewMemberModal";
import ExcludeMemberPreviewModal from "../components/common/ExcludeMemberPreviewModal";
import ExcludeMemberUpload from "../components/common/ExcludeMemberUpload";
import TargetMemberPreviewModal from "../components/common/TargetMemberPreviewModal";
import TargetMemberUpload from "../components/common/TargetMemberUpload";
import BillingModal from "../components/common/BillingModal";
import { parseExcludeMemberFile } from "../utils/excludeMember";
import {
  annotateTargetMemberMatchStatus,
  TARGET_MEMBER_FOUND,
  TARGET_MEMBER_NOT_FOUND,
} from "../utils/targetMember";
import {
  calculateDeliverySeconds,
  calculateCampaignEndDate,
} from "../utils/campaignSchedule";
import {
  getDefaultMessage,
  getWhatsappFooterText,
  PROFILE_COMPLETION_REMINDER_SMS_FOOTER,
} from "../constants/defaultMessages";
import { DEFAULT_CAMPAIGN_IMAGES } from "../constants/images";

const { Title, Text } = Typography;

const FieldRow: React.FC<{
  label: React.ReactNode;
  required?: boolean;
  children: React.ReactNode;
}> = ({ label, required, children }) => (
  <Row gutter={16} align="top" style={{ marginBottom: 12 }}>
    <Col xs={24} md={10} lg={8}>
      <Text>
        {label} {required && <Text type="danger">*</Text>}
      </Text>
    </Col>
    <Col xs={24} md={14} lg={16}>
      {children}
    </Col>
  </Row>
);

const UpdateCampaign: React.FC = () => {
  // ======================================================
  // 📱 Device Detection
  // ======================================================
  const isMobileOrTablet = window.innerWidth <= 1024;

  // ======================================================
  // 📝 Form & Navigation
  // ======================================================
  const [form] = Form.useForm<CampaignFormValues>();
  const { brandId, campaignInfo, setCampaignInfo } = useBrandStore();

  // ======================================================
  // 📊 UI State Management
  // ======================================================
  const [scheduleType, setScheduleType] = useState<number>(TypeEnum.Onetime);
  const [endScheduleType, setEndScheduleType] = useState<number>(
    EndScheduleEnum.OnSpecificDate,
  );
  const [targetMemberMode, setTargetMemberMode] = useState<number>(
    ModeEnum.Refresh,
  );
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [evoucherCustom, setEvoucherCustom] = useState(false);
  const [firstReloadMember, setFirstReloadMember] = useState(true);
  const [changeRFM, setChangeRFM] = useState(false);
  const [isBillingModalOpen, setIsBillingModalOpen] = useState(false);

  const [isCampaignSuccessModalOpen, setIsCampaignSuccessModalOpen] =
    useState(false);
  const [isCampaignFailedModalOpen, setIsCampaignFailedModalOpen] =
    useState(false);
  const [pendingPayload, setPendingPayload] = useState<CampaignDetails | null>(
    null,
  );
  const [loadingVoucher, setLoadingVoucher] = useState(false);
  const [link, setLink] = useState("");
  const [enableTrackingLink, setEnableTrackingLink] = useState<boolean>(true);

  const [listMessageTemplates] = useState<MessageTemplateData[]>([]);
  const [nameError, setNameError] = useState<string | null>("");
  const [selectedTemplate, setSelectedTemplate] = useState<TemplatesTypeEnum>();
  const [inactiveDays, setInactiveDays] = useState(60);
  const [incompleteProfileFieldOptions, setIncompleteProfileFieldOptions] =
    useState<IncompleteProfileFieldOption[]>(INCOMPLETE_PROFILE_FIELD_OPTIONS);
  // Becomes true only once the real field list has been fetched (or the
  // fetch has failed) — guards the default-selection effect below so it
  // never seeds values from the hardcoded fallback's key names, which
  // don't match the real API's (e.g. "Birthday" vs "DateOfBirth").
  const [incompleteProfileFieldsReady, setIncompleteProfileFieldsReady] =
    useState(false);

  const [
    isModalWarningCreateCampaignVisible,
    setIsModalWarningCreateCampaignVisible,
  ] = useState(false);
  const [selectedCampaignChannel, setSelectedCampaignChannel] =
    useState<ChannelEnum | null>(null);
  const [messageTemplatesOpen, setMessageTemplatesOpen] = useState(false);
  const [selectedMessageTemplates, setSelectedMessageTemplates] =
    useState<MessageTemplateData | null>(null);
  const templatesSummary = selectedMessageTemplates
    ? `${selectedMessageTemplates.Name}`
    : "No templates selected";
  const [loading, setLoading] = useState(false);
  const [loadingRefreshMember, setLoadingRefreshMember] = useState(false);
  const [estimatedTotalMember, setEstimatedTotalMember] = useState(0);
  const [estimatedMember, setEstimatedMember] = useState<any[]>([]);
  const [totalMembersError, setTotalMembersError] = useState<string | null>(
    null,
  );
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const openPreview = () => setIsPreviewOpen(true);
  const closePreview = () => setIsPreviewOpen(false);
  const [segmentLinkIdBySegmentId, setSegmentLinkIdBySegmentId] = useState<
    Map<number, number>
  >(new Map());
  const [postalLinkIdByDistrictId, setPostalLinkIdByDistrictId] = useState<
    Map<number, number>
  >(new Map());
  const [, setMessageId] = useState<number | null>(null);
  const [selectedVoucher, setSelectedVoucher] = useState<Voucher | null>(null);
  const [selectedEVouchers, setSelectedEVouchers] = useState<EVoucher[]>([]);
  // True once the user explicitly clears the "not found" fallback below,
  // so it doesn't reappear on re-render (data.SKUList never changes).
  const [eVoucherFallbackCleared, setEVoucherFallbackCleared] = useState(false);
  // Draft selection while the picker modal is open — only committed to
  // selectedEVouchers on Submit; discarded if the modal is closed/cancelled.
  const [draftEVouchers, setDraftEVouchers] = useState<EVoucher[]>([]);
  // Collapsed by default when summarizing 3+ selected bundles — "Show N more"
  // reveals the rest inline instead of always listing every bundle.
  const [evoucherListExpanded, setEvoucherListExpanded] = useState(false);
  const openVoucherModal = () => {
    setDraftEVouchers(selectedEVouchers);
    setVoucherOpen(true);
  };
  const closeVoucherModal = () => {
    setSelectedKey(null);
    setSelectedRow(null);
    setDraftEVouchers([]);
    setVoucherOpen(false);
  };
  const [postalFilter, setPostalFilter] = useState<string[]>([]);
  const [alreadyReload, setAlreadyReload] = useState(false);
  const [searchVoucher, setSearchVoucher] = useState<string>("");
  const [selectedKey, setSelectedKey] = useState<React.Key | null>(null);
  const [selectedRow, setSelectedRow] = useState<Voucher | null>(null);
  const [, setCampaignDetails] = useState<CampaignDetails | null>(null);
  const startOfToday = dayjs().startOf("day");
  const [dataPostalDistrict, setDataPostalDistrict] = useState<
    PostalDistricts[]
  >([]);
  const [listBlockDate, setListBlockDate] = useState<DateRange[]>([]);
  const [listVoucher, setListVoucher] = useState<Voucher[]>([]);
  const [listEVoucher, setListEVoucher] = useState<EVoucher[]>([]);
  const filteredVouchers = listVoucher.filter((v) =>
    v.Name?.toLowerCase().includes(searchVoucher.toLowerCase()),
  );
  const filteredVEouchers = listEVoucher.filter((v) =>
    v.Name?.toLowerCase().includes(searchVoucher.toLowerCase()),
  );
  const blocked: BlockRange[] = parseBlockedRanges(
    listBlockDate,
    "YYYY-MM-DD HH:mm",
  );
  const { search } = useLocation();
  const navigateWithQuery = useQueryNavigator();
  const { Option } = Select;

  const query = new URLSearchParams(search);
  const id = query.get("id");
  const [data, setData] = useState<CampaignDetails | null>(null);
  const isBilled = data?.CampaignRunLog?.some((log) => log.IsBilled === true);

  const selectedType = Form.useWatch("Type", form) ?? 0.0;
  const isProfileCompletionReminder =
    selectedType === CampaignTypeEnum.ProfileCompletionReminder;

  useEffect(() => {
    if (!isProfileCompletionReminder) return;
    setIncompleteProfileFieldsReady(false);
    campaignService
      .getIncompleteProfileFields()
      .then((data) => {
        if (data && data.length) setIncompleteProfileFieldOptions(data);
      })
      .finally(() => setIncompleteProfileFieldsReady(true));
  }, [isProfileCompletionReminder]);

  useEffect(() => {
    if (!isProfileCompletionReminder || !incompleteProfileFieldsReady) return;
    if (form.getFieldValue("IncompleteProfileFields") !== undefined) return;
    const defaultValues = incompleteProfileFieldOptions
      .filter((f) => f.defaultChecked)
      .map((f) => f.value);
    if (defaultValues.length) {
      form.setFieldsValue({ IncompleteProfileFields: defaultValues });
    }
  }, [
    isProfileCompletionReminder,
    incompleteProfileFieldsReady,
    incompleteProfileFieldOptions,
    form,
  ]);

  const isMessageUserEdited = useRef(false);
  const emailQuillRefs = useRef<
    Map<string, import("../components/common/EditorQuill").EditorQuillRef>
  >(new Map());
  const activeEmailFieldRef = useRef<string | null>(null);
  const shortenLinkTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const campaignDateRef = useRef<HTMLDivElement>(null);

  const [showTimeout, setShowTimeout] = useState(false);

  // ======================================================
  // 📧 Email Preview & Template
  // ======================================================
  const [emailPreviewHtml, setEmailPreviewHtml] = useState<string | null>(null);
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [parsedEmailTemplate, setParsedEmailTemplate] =
    useState<ParsedEmailTemplate | null>(null);
  const [rawEmailTemplate, setRawEmailTemplate] = useState<string | null>(null);
  const [emailFieldValues, setEmailFieldValues] = useState<
    Record<string, string>
  >({});
  const [savedEmailContent, setSavedEmailContent] = useState<string | null>(
    null,
  );
  const voucherVars = ["{VoucherName}", "{VoucherExpiry}"];
  const evoucherVars = ["{EVoucherName}", "{EVoucherPrice}"];

  const filteredVars =
    selectedType === CampaignTypeEnum.EVoucher
      ? [...VARS.filter((v) => !voucherVars.includes(v)), ...evoucherVars]
      : selectedType === CampaignTypeEnum.Voucher ||
          selectedType === CampaignTypeEnum.VoucherPoint
        ? selectedVoucher?.RedemptionDateBasedOn === 2
          ? [...VARS, "{VoucherValidDays}"]
          : VARS
        : isProfileCompletionReminder
          ? [
              ...VARS.filter((v) => !voucherVars.includes(v)),
              ...PROFILE_COMPLETION_REMINDER_VARS,
            ]
          : VARS.filter((v) => !voucherVars.includes(v));
  // ======================================================
  // 📂 Criteria & RFM Selections
  // ======================================================
  const criteriaProfileValue = useWatch("Profile", form);
  const periodValue = useWatch("Period", form);
  const segmentValue = useWatch("Segments", form);
  const incompleteProfileFieldsValue: string[] =
    useWatch("IncompleteProfileFields", form) ?? [];
  const frequencyValue = useWatch("Frequency", form);
  const scheduleEndTypeValue = Form.useWatch("ScheduleEndType", form);
  const maxOccurrencesValue = Form.useWatch("MaxOccurrences", form);
  const repeatDaysValue = Form.useWatch("RepeatDays", form);
  const repeatMonthValue = Form.useWatch("RepeatMonth", form);
  const endDateValue = Form.useWatch("EndDate", form);

  const minExtensionDays =
    scheduleType === TypeEnum.Onetime
      ? 30
      : frequencyValue === TypeEnum.Recurring_Monthly
        ? 7
        : 3;
  const maxExtensionDays =
    scheduleType === TypeEnum.Onetime
      ? 60
      : frequencyValue === TypeEnum.Recurring_Monthly
        ? 30
        : 7;

  const hasCriteria =
    criteriaProfileValue !== null && criteriaProfileValue !== undefined;
  const hasPeriod = !!periodValue;
  const hasSegment = Array.isArray(segmentValue)
    ? segmentValue.length > 0
    : !!segmentValue;
  // ======================================================
  // 📑 RFM Options (Segments / Profiles / Postal Codes)
  // ======================================================
  const AllBrand = ALL_BRAND;

  const [selectedPurchaseBrand, setSelectedPurchaseBrand] = useState<string[]>(
    [],
  );
  const [listPurchaseBrandFilter, setListPurchaseBrandFilter] = useState<any[]>(
    [],
  );
  const [listBrandMemberSegment, setListBrandMemberSegment] = useState<
    { Id: number; Name: string }[]
  >([]);
  const [listBrandRFMProfile, setListBrandRFMProfile] = useState<
    { Id: number; Name: string; IsActive: boolean }[]
  >([]);

  const SegmentOptions: SelectProps["options"] = useMemo(
    () => listBrandMemberSegment.map((s) => ({ label: s.Name, value: s.Id })),
    [listBrandMemberSegment],
  );

  const ProfileOptions: SelectProps["options"] = useMemo(
    () =>
      [...listBrandRFMProfile].map((s) => ({
        label: s.IsActive ? `${s.Name} (Currently Selected)` : s.Name,
        value: s.Id,
      })),
    [listBrandRFMProfile],
  );

  const allSegmentIds = useMemo(
    () =>
      (SegmentOptions ?? [])
        .map((option) => option.value)
        .filter((value): value is number => typeof value === "number"),
    [SegmentOptions],
  );
  const activeProfileId = listBrandRFMProfile?.find((s) => s.IsActive)?.Id;

  const selectedPostalSectors = useMemo<string[]>(() => {
    if (!Array.isArray(dataPostalDistrict) || !postalFilter?.length) return [];

    const idSet = new Set(postalFilter.map((v) => String(v)));

    const merged = dataPostalDistrict
      .filter((d) => idSet.has(String(d.Id)))
      .flatMap((d) => d.PostalSectors ?? []);

    return Array.from(new Set(merged));
  }, [postalFilter, dataPostalDistrict]);

  const PostalCodeOptions: SelectProps["options"] = useMemo(
    () =>
      dataPostalDistrict.map((s) => ({
        label: `${s.PostalSectors.join(", ")} – ${s.GeneralLocation}`,
        value: String(s.Id),
        search: `${s.PostalSectors.join(" ")} ${s.GeneralLocation}`,
      })),
    [dataPostalDistrict],
  );
  // ======================================================
  // 💬 Message Handling
  // ====================================================
  const msg = Form.useWatch("Message", form) ?? "";
  // const charCount = htmlToWhatsappText(msg || "").length;
  const isEVoucher = selectedType === CampaignTypeEnum.EVoucher;

  const evoucherCTAText = Form.useWatch("CustomCopyText", form);

  const effectiveActionText =
    isEVoucher && evoucherCustom && evoucherCTAText
      ? evoucherCTAText
      : "View here";
  // const effectiveCTAUrl = link;
  // CTA link now uses a static placeholder — see GXT-179 (per-member JWT click tracking)
  const effectiveCTAUrl = "https://getz.co/a/CTALINK";
  const additionalMessageText = isProfileCompletionReminder
    ? PROFILE_COMPLETION_REMINDER_SMS_FOOTER
    : `\n\n${effectiveActionText}:\n${effectiveCTAUrl}\n\nTo Unsub:\nhttps://getz.co/a/UNSUB`;
  const additionalMessageWhatsappText = getWhatsappFooterText(selectedType);
  let finalMessage = msg + (enableTrackingLink ? additionalMessageText : "");
  finalMessage = finalMessage.replace(/\r\n/g, "\n");
  const finalHtmlMessage = msg;
  const charCount = getSmsCharCount(finalMessage);

  const isUnicodeText = !isGsmText(finalMessage);
  const smsSegments = getSmsSegments(finalMessage, isUnicodeText);

  const previewHtml = finalMessage
    ? DOMPurify.sanitize(
        selectedCampaignChannel === ChannelEnum.Sms
          ? finalMessage.replace(/\n/g, "<br />")
          : finalHtmlMessage,
      )
    : "Your Message will appear here...";

  const insertVar = (v: string) => {
    if (selectedCampaignChannel === ChannelEnum.Email) {
      const fieldName = activeEmailFieldRef.current;
      if (fieldName) {
        emailQuillRefs.current
          .get(fieldName)
          ?.insertTextAtCursor(resolveEVoucherVars(resolveVoucherVars(v)));
      }
      return;
    }

    const html = form.getFieldValue("Message") || "";
    const lower = html.toLowerCase();
    const closeIdx = lower.lastIndexOf("</p>");

    let next: string;

    if (closeIdx !== -1) {
      const before = html.slice(0, closeIdx);
      const after = html.slice(closeIdx);

      const needsSpace =
        before.length > 0 && !/[ \t\n]$/.test(before.slice(-1));
      const spacer = needsSpace ? " " : "";

      next = before + spacer + v + after;
    } else {
      const needsSpace = html.length > 0 && !/[ \t\n]$/.test(html);
      next = html + (needsSpace ? " " : "") + v;
    }

    form.setFieldsValue({
      Message: resolveEVoucherVars(resolveVoucherVars(next)),
    });
  };

  // ======================================================
  // 📆 Campaign Details
  // ======================================================

  const campaignName = Form.useWatch("Name", form) ?? 0.0;
  // NOTE: `Form.useWatch("CampaignDate")` was not re-rendering on change under
  // antd 5 + React 19, so the derived start/end + submit value stayed stale.
  // Drive it from local state synced via the DatePicker's onChange instead.
  const [campaignDate, setCampaignDate] = useState<dayjs.Dayjs | null>(null);
  const cost = Form.useWatch("CostPerMember", form) ?? 0.0;
  const budget = Form.useWatch("Budget", form) ?? 0;

  const maxReach = useMemo(() => {
    const budgetFinal = budget ?? 0;

    if (selectedCampaignChannel === ChannelEnum.Sms) {
      const realCostPerMember = cost * smsSegments;
      return realCostPerMember > 0
        ? Math.floor(budgetFinal / realCostPerMember)
        : 0;
    }

    return cost > 0 ? Math.floor(budgetFinal / cost) : 0;
  }, [budget, cost, smsSegments, selectedCampaignChannel]);

  const { formattedStart, formattedEnd } = useMemo(() => {
    if (!campaignDate) {
      return { formattedStart: "", formattedEnd: "" };
    }

    return {
      formattedStart: dayjs(campaignDate).format("YYYY-MM-DD HH:mm"),
      formattedEnd: calculateCampaignEndDate(
        campaignDate,
        estimatedTotalMember,
        selectedCampaignChannel,
        scheduleType !== TypeEnum.Onetime
          ? {
              scheduleEndType: scheduleEndTypeValue,
              endDate: endDateValue,
              maxOccurrences: maxOccurrencesValue,
              repeatDays: repeatDaysValue,
              repeatMonth: repeatMonthValue,
              frequency: frequencyValue,
            }
          : undefined,
      ),
    };
  }, [
    campaignDate,
    estimatedTotalMember,
    selectedCampaignChannel,
    scheduleType,
    scheduleEndTypeValue,
    endDateValue,
    maxOccurrencesValue,
    repeatDaysValue,
    repeatMonthValue,
    frequencyValue,
  ]);
  // ======================================================
  // 🔄 Member Refresh Logic
  // ======================================================

  const onChangeRFM = () => {
    setChangeRFM(true);
    setAlreadyReload(false);
  };

  const onChangeTemplate = (value: any) => {
    if (value === TemplatesTypeEnum.Default) {
      form.setFieldsValue({ InactiveDays: 60 });
      setInactiveDays(60);
    }
    if (value === TemplatesTypeEnum.UploadedMemberList) {
      // RFM-only concepts don't apply to a fixed uploaded list.
      handleRemoveExclude();
      setSelectedPurchaseBrand([AllBrand]);
    } else {
      // Leaving Uploaded Member List — drop the uploaded target list so it
      // doesn't leak into RFM-based estimates/submit.
      handleRemoveTarget();
    }
    setSelectedTemplate(value);

    onChangeRFM();
  };

  const postalTotal = useMemo(
    () => PostalCodeOptions.filter((o) => o.value !== "all").length,
    [PostalCodeOptions],
  );

  // `overrideTargetFile` lets callers (e.g. the target member upload handler)
  // pass a just-parsed File directly, since relying on `targetRawFile` state
  // would read a stale value from the same render's closure. Same story for
  // `overrideTargetRows` vs `targetRows`.
  const handleRefreshMember = async (
    overrideTargetFile?: File,
    overrideTargetRows?: Record<string, any>[],
  ) => {
    setLoadingRefreshMember(true);

    try {
      if (firstReloadMember) {
        setFirstReloadMember(false);
      }
      const isUploadedMemberList =
        selectedTemplate === TemplatesTypeEnum.UploadedMemberList;
      const fileToCheck = overrideTargetFile ?? targetRawFile ?? undefined;
      const effectiveTargetRows = overrideTargetRows ?? targetRows;

      let result: { EstimatedCountMember: number; ListMember: any[] };

      if (
        isUploadedMemberList &&
        !fileToCheck &&
        effectiveTargetRows.length > 0 &&
        "Status" in effectiveTargetRows[0]
      ) {
        // No new file uploaded this session — the backend already told us
        // who matched when the list was originally saved, so there's
        // nothing new to check against.
        result = {
          EstimatedCountMember: effectiveTargetRows.filter(
            (r) => r.Status === TARGET_MEMBER_FOUND,
          ).length,
          ListMember: [],
        };
      } else {
        const data: EstimatedTotalMembersRequest = {
          BrandId: brandId ?? "",
          RFMProfile: isProfileCompletionReminder
            ? (activeProfileId ?? 0)
            : (criteriaProfileValue ?? 0),
          RFMPeriod: isProfileCompletionReminder
            ? RFMPeriod.Lifetime
            : (periodValue ?? RFMPeriod.Lifetime),
          SegmentIds: isProfileCompletionReminder
            ? allSegmentIds
            : (segmentValue ?? []),
          IncludeListMember: false,
          FromAge: form.getFieldValue("ageRange")?.from,
          ToAge: form.getFieldValue("ageRange")?.to,
          PostCode: selectedPostalSectors,
          PurchaseBrand: selectedPurchaseBrand.includes(AllBrand)
            ? null
            : selectedPurchaseBrand,
          CampaignType: selectedType,
          TemplateType: selectedTemplate,
          InactiveDays:
            selectedTemplate === TemplatesTypeEnum.Default ? inactiveDays : 0,
          IncompleteProfileFields: isProfileCompletionReminder
            ? form.getFieldValue("IncompleteProfileFields")
            : undefined,
        };
        result = await campaignService.estimatedTotalMember(
          data,
          excludeRawFile ?? undefined,
          fileToCheck,
        );
        if (!result) {
          message.error(
            "Failed to estimate total members. Please try again.",
          );
          return null;
        }

        if (isUploadedMemberList) {
          // Backend doesn't (yet) filter EstimatedCountMember by the
          // uploaded file, so correct it using the same row-level match the
          // preview modal uses.
          const annotated = annotateTargetMemberMatchStatus(
            effectiveTargetRows,
            result.ListMember ?? [],
          );
          result.EstimatedCountMember = annotated.filter(
            (r) => r.Status === TARGET_MEMBER_FOUND,
          ).length;
        }
      }

      setEstimatedTotalMember(result.EstimatedCountMember);
      setEstimatedMember(result.ListMember);
      if (result.EstimatedCountMember > 0) {
        setTotalMembersError(null);
      }
      setChangeRFM(false);
      setAlreadyReload(true);
      return result;
    } catch (error) {
      console.error("Failed to refresh member:", error);
      return null;
    } finally {
      setLoadingRefreshMember(false);
    }
  };

  // ======================================================
  // 📅 Schedule Type Switching
  // ======================================================
  const onScheduleChange = (e: RadioChangeEvent) => {
    const val = e.target.value;

    if (
      scheduleType === TypeEnum.Onetime &&
      val === TypeEnum.Recurring_Weekly
    ) {
      form.setFieldsValue({
        Frequency: TypeEnum.Recurring_Weekly,
        ScheduleEndType: EndScheduleEnum.OnSpecificDate,
      });
    }

    setScheduleType(val);
  };

  const onModeChange = (e: RadioChangeEvent) => {
    const val = e.target.value;
    setTargetMemberMode(val);
  };

  const onEndScheduleChange = (e: RadioChangeEvent) => {
    setEndScheduleType(e.target.value);
  };

  // ======================================================
  // 🚀 Form Submission
  // ======================================================
  const handleSubmit = async (values: CampaignFormValues) => {
    try {
      const estimated = await handleRefreshMember();
      if (!estimated) return;

      if (estimated.EstimatedCountMember === 0) {
        setTotalMembersError(
          selectedTemplate === TemplatesTypeEnum.UploadedMemberList
            ? "None of the uploaded members matched a registered account. Please check the file and try again."
            : "No members matched the selected criteria.",
        );
        form.scrollToField("Name" as any, {
          behavior: "smooth",
          block: "start",
        });
        return;
      }
      // const nextEstimatedCost =
      //   selectedCampaignChannel === ChannelEnum.Sms
      //     ? estimated.EstimatedCountMember * cost * smsSegments
      //     : estimated.EstimatedCountMember * cost;
      const formattedCampaignEndDate = calculateCampaignEndDate(
        formattedStart,
        estimated.EstimatedCountMember,
        selectedCampaignChannel,
        scheduleType !== TypeEnum.Onetime
          ? {
              scheduleEndType: values.ScheduleEndType,
              endDate: values.EndDate,
              maxOccurrences: values.MaxOccurrences,
              repeatDays: values.RepeatDays,
              repeatMonth: values.RepeatMonth,
              frequency: values.Frequency,
            }
          : undefined,
      );

      // const resolvedCampaignName = values.Name.trim();
      // const resolvedCustomUrl =
      //   isEVoucher && evoucherCustom ? values.CustomUrl?.trim() : undefined;
      // let resolvedLink = link;
      // if (
      //   campaignInfo?.MemberPortalUrl &&
      //   resolvedCampaignName &&
      //   selectedCampaignChannel
      // ) {
      //   const generatedLink = `${campaignInfo.MemberPortalUrl}?utm_source=${ChannelKeyMap[selectedCampaignChannel]}&utm_campaign=${resolvedCampaignName}&utm_medium=MemberPortal${resolvedCustomUrl ? `&targetedUrl=${encodeURIComponent(resolvedCustomUrl)}` : ""}`;
      //   resolvedLink = (await fetchShortenLink(generatedLink)) ?? link;
      // }
      // CTA link now uses a static placeholder — see GXT-179 (per-member JWT click tracking)
      const resolvedLink = "https://getz.co/a/CTALINK";
      const resolvedAdditionalMessageText = isProfileCompletionReminder
        ? PROFILE_COMPLETION_REMINDER_SMS_FOOTER
        : `\n\n${effectiveActionText}:\n${resolvedLink}\n\nTo Unsub:\nhttps://getz.co/a/UNSUB`;

      const payload: CampaignDetails = {
        Id: Number(id) ?? 0,
        Name: values.Name,
        Type: values.Type,
        RFMPeriod: isProfileCompletionReminder
          ? RFMPeriod.Lifetime
          : values.Period,
        FromAge: values.ageRange?.from,
        ToAge: values.ageRange?.to,
        Messages: [
          {
            Content:
              selectedCampaignChannel === ChannelEnum.WhatsAppStandard
                ? htmlToWhatsappText(values?.Message ?? "") +
                  "\n" +
                  additionalMessageWhatsappText
                : selectedCampaignChannel === ChannelEnum.WhatsAppPremium
                  ? (selectedMessageTemplates?.Content ?? "")
                  : selectedCampaignChannel === ChannelEnum.Email
                    ? (composedEmailPreview ?? values?.Message ?? "")
                    : (values?.Message ?? "") + resolvedAdditionalMessageText,
            Link: resolvedLink,
            CampaignTemplates:
              selectedCampaignChannel === ChannelEnum.WhatsAppPremium
                ? selectedMessageTemplates
                : null,
            CustomCopyText:
              isEVoucher && evoucherCustom
                ? (values.CustomCopyText ?? undefined)
                : undefined,
            CustomUrl:
              isEVoucher && evoucherCustom
                ? (values.CustomUrl ?? undefined)
                : undefined,
            MessageSubject:
              selectedCampaignChannel === ChannelEnum.Email
                ? values.MessageSubject
                : undefined,
            MessagePreheader:
              selectedCampaignChannel === ChannelEnum.Email
                ? values.MessagePreheader
                : undefined,
          },
        ],

        Segments: (
          (isProfileCompletionReminder ? allSegmentIds : values.Segments) ?? []
        ).map((segId) => ({
          SegmentId: Number(segId),
          Id: segmentLinkIdBySegmentId.get(Number(segId)) ?? undefined,
        })),
        PostalDistricts: (values.PostalCode ?? []).map((districtId) => ({
          DistrictId: Number(districtId),
          Id: postalLinkIdByDistrictId.get(Number(districtId)) ?? undefined,
        })),
        RFMProfile: isProfileCompletionReminder
          ? activeProfileId
          : values.Profile,
        BrandId: brandId ?? undefined,
        Status: values.Status,
        VoucherId:
          selectedType === CampaignTypeEnum.VoucherPoint ||
          selectedType === CampaignTypeEnum.Voucher
            ? selectedVoucher?.Id
            : undefined,
        EVoucherSkus:
          selectedEVouchers.length > 0
            ? selectedEVouchers
                .filter((v) => v.SKU)
                .map((v) => ({ SKU: v.SKU }))
            : eVoucherNotFoundCount > 0
              ? getCampaignEVoucherSkus(data).map((sku) => ({ SKU: sku }))
              : undefined,
        CampaignStart: formattedStart,
        CampaignEnd: formattedCampaignEndDate,
        Budget: values.Budget,
        EstimatedTotalMembers: estimated.EstimatedCountMember,
        ScheduleType:
          scheduleType === TypeEnum.Onetime
            ? TypeEnum.Onetime
            : values.Frequency,
        ImageUrl: previewUrl
          ? previewUrl.startsWith("/")
            ? `${window.location.origin}${previewUrl}`
            : previewUrl
          : undefined,
        Channel: selectedCampaignChannel ?? undefined,
        PurchaseBrand: selectedPurchaseBrand.includes(AllBrand)
          ? null
          : JSON.stringify(selectedPurchaseBrand),
        ExcludeMembers: excludeRawFile
          ? null
          : excludeMembers.length
            ? excludeMembers
            : null,
        ClearExclusionList:
          originalHadExcludeMembers &&
          excludeFileList.length === 0 &&
          excludeRawFile === null,
        TargetMembers: targetRawFile
          ? null
          : targetMembers.length
            ? targetMembers
            : null,
        ClearTargetMembersList:
          originalHadTargetMembers &&
          targetFileList.length === 0 &&
          targetRawFile === null,
        TemplateType: selectedTemplate,
        InactiveDays:
          selectedTemplate === TemplatesTypeEnum.Default ? inactiveDays : 0,
        IncompleteProfileFields: isProfileCompletionReminder
          ? values.IncompleteProfileFields
          : undefined,
        Mode:
          scheduleType !== TypeEnum.Onetime
            ? ModeEnum.Refresh
            : targetMemberMode,
        Retargeting:
          scheduleType !== TypeEnum.Onetime ? values.Retargeting : undefined,
        ExtensionDays: values.ExtensionDays ?? undefined,
        IsBilled: 0,
        ScheduleEndType:
          values.Frequency !== TypeEnum.Onetime
            ? values.ScheduleEndType
            : undefined,
        RepeatMonth:
          values.Frequency !== TypeEnum.Onetime
            ? values.RepeatMonth
            : undefined,
        RepeatDays:
          values.Frequency === TypeEnum.Recurring_Weekly
            ? JSON.stringify(values.RepeatDays)
            : undefined,
        EndDate:
          values.ScheduleEndType === EndScheduleEnum.OnSpecificDate
            ? dayjs(values.EndDate).format("YYYY-MM-DD HH:mm")
            : undefined,
        MaxOccurrences:
          values.ScheduleEndType === EndScheduleEnum.AfterOccurrences
            ? values.MaxOccurrences
            : undefined,
        EstimatedIntervalTime: calculateDeliverySeconds(
          estimated.EstimatedCountMember,
          selectedCampaignChannel,
        ),
        EnableCustomTrackingLink: isEVoucher ? evoucherCustom : undefined,
      };
      setCampaignDetails(payload);
      setPendingPayload(payload);
      setIsBillingModalOpen(true);
    } catch (err) {
      console.error(err);
      return Promise.reject(err);
    }
  };

  // Helper
  // const isChannelEnum = (v: unknown): v is ChannelEnum =>
  //   typeof v === "number" && v in ChannelEnum;

  // ======================================================
  // 🗂 Data Fetching
  // ======================================================
  const fetchListAccountBrand = async () => {
    if (brandId) {
      try {
        setLoadingVoucher(true);
        const data = await campaignService.getListAccountBrand(brandId);
        if (data) {
          setListPurchaseBrandFilter(
            Array.from(
              new Set(
                data
                  .map((s: any) => s.BrandName?.trim())
                  .filter((name: any) => name && name !== ""),
              ),
            ).sort((a: any, b: any) => a.localeCompare(b)),
          );
        }
      } catch (error) {
        console.error("Error fetching account brands:", error);
      } finally {
        setLoadingVoucher(false);
      }
    }
  };
  const fetchVoucherList = async () => {
    if (brandId) {
      try {
        setLoadingVoucher(true);
        const data = await campaignService.getAllVouchers();
        if (data) {
          setListVoucher(data);
        }
      } catch (error) {
        console.error("Error fetching vouchers:", error);
      } finally {
        setLoadingVoucher(false);
      }
    }
  };

  const fetchEVoucherList = async () => {
    if (brandId) {
      try {
        setLoadingVoucher(true);
        let email = campaignInfo?.Email ?? "";
        const accounts = await billingService.getCreditCard();
        if (accounts && accounts.length > 0) {
          email = accounts.map((a) => a.email).join(",");
        }
        const dataEVoucher = await campaignService.getAllEVouchersV2(email);
        if (dataEVoucher) {
          const mapped = mapToEVouchers(dataEVoucher);
          setListEVoucher(mapped);
        }
      } catch (error) {
        console.error("Error fetching E-Voucher:", error);
      } finally {
        setLoadingVoucher(false);
      }
    }
  };

  const fetchBlockDate = async () => {
    if (brandId && selectedCampaignChannel) {
      try {
        const data = await campaignService.getBlockedCampaignDateRanges(
          brandId,
          selectedCampaignChannel,
        );
        if (data) {
          setListBlockDate(data);
        }
      } catch (error) {
        console.error("Error fetching block dates:", error);
      }
    }
  };
  const fetchPostalDistrict = async () => {
    if (brandId) {
      try {
        const data = await campaignService.getAllPostalDistrict();
        if (data) {
          setDataPostalDistrict(data);
        }
      } catch (error) {
        console.error("Error fetching postal district:", error);
      }
    }
  };
  const fetchBrandMemberSegment = async () => {
    if (brandId) {
      try {
        const data =
          await brandMemberSegmentService.listBrandMemberSegment(brandId);
        if (data) {
          setListBrandMemberSegment(data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  };

  const fetchBrandRFMProfile = async () => {
    if (brandId) {
      try {
        const data = await brandRFMProfileService.getBrandRFMProfile(brandId);
        if (data) {
          setListBrandRFMProfile(data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  };

  const fetchLinkOS = async () => {
    if (brandId) {
      try {
        await helperService.getPrimaryDomainByBrandId(brandId);
      } catch (error) {
        console.error("Error get link OS:", error);
      }
    }
  };

  const FetchCheckAvailableCampaignName = async (value: string) => {
    if (brandId) {
      try {
        const isAvailable = await campaignService.checkAvailableCampaignName(
          value,
          data?.Id ?? undefined,
        );
        return isAvailable;
      } catch (error) {
        console.error("Error Check Available Campaign Name:", error);
      }
    }
  };

  const handleOpenEmailPreview = async () => {
    if (!selectedType || !brandId) return;
    setEmailPreviewLoading(true);
    setEmailPreviewOpen(true);
    const isVoucherType =
      selectedType === CampaignTypeEnum.Voucher ||
      selectedType === CampaignTypeEnum.VoucherPoint;
    const rawHtml = await campaignService.getEmailPreview(
      selectedType,
      brandId,
      isVoucherType ? selectedVoucher?.Id : undefined,
    );
    if (rawHtml) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(rawHtml, "text/html");
      doc
        .querySelectorAll<HTMLElement>('[data-section="editable"]')
        .forEach((el) => {
          const fieldName = el.getAttribute("data-field") ?? "Message";
          const userHtml = emailFieldValues[fieldName] ?? "";
          const td = el.querySelector<HTMLElement>("td");
          const target = td ?? el;
          if (td) {
            target.innerHTML = userHtml;
          } else {
            target.innerHTML = quillHtmlToInner(userHtml, el.tagName);
          }
          target.style.outline = "2px dashed #1677ff";
          target.style.outlineOffset = "4px";
          target.style.position = "relative";
          const badge = doc.createElement("div");
          badge.style.cssText =
            "position:absolute;top:4px;right:4px;background:#1677ff;color:#fff;font-size:10px;padding:2px 6px;border-radius:4px;font-family:Arial,sans-serif;z-index:10;";
          badge.textContent = "✏ Message Content";
          target.appendChild(badge);
        });
      if (link) {
        doc.querySelectorAll<HTMLAnchorElement>("a").forEach((a) => {
          const text = a.textContent?.trim() ?? "";
          if (text.includes("View Reward Details")) {
            a.href = link;
          } else if (
            text.startsWith("http") &&
            a.closest("p")?.textContent?.includes("button above")
          ) {
            a.href = link;
            a.textContent = link;
          }
        });
      }
      setEmailPreviewHtml(doc.documentElement.outerHTML);
    } else {
      setEmailPreviewHtml(null);
    }
    setEmailPreviewLoading(false);
  };

  const fetchShortenLink = async (value: string): Promise<string | null> => {
    // CTA link (member portal / shortlink) fetch disabled — using a static
    // placeholder until per-member JWT click tracking is implemented (GXT-179).
    // if (brandId) {
    //   try {
    //     const data = await helperService.getShortenLink(value);
    //     if (data) {
    //       setLink(data);
    //       return data;
    //     }
    //   } catch (error) {
    //     console.error("Error get shorten link:", error);
    //   }
    // }
    return null;
  };

  //   const fetchMessageTemplates = async () => {
  //   if (brandId) {
  //     try {
  //       const data = await campaignService.getAllMessageTemplates();
  //       if (data) {
  //         setListMessageTemplates(data);
  //       }
  //     } catch (error) {
  //       console.error("Error fetch message templates:", error);
  //     }
  //   }
  // };

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [, setUploadingFile] = useState<UploadFile | null>(null);
  const [progressUpload, setProgressUpload] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [notif, contextHolder] = notification.useNotification();

  const composedEmailPreview = useMemo(() => {
    if (!rawEmailTemplate || selectedCampaignChannel !== ChannelEnum.Email)
      return null;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(rawEmailTemplate, "text/html");

      if (previewUrl) {
        doc.querySelectorAll<HTMLImageElement>("img").forEach((img) => {
          const cls =
            img.getAttribute("class") ?? img.getAttribute("className") ?? "";
          if (cls.includes("banner-image")) img.src = previewUrl;
        });
        doc.querySelectorAll<HTMLElement>("div").forEach((div) => {
          const s = (div.getAttribute("style") ?? "").replace(/\s/g, "");
          if (s.includes("position:absolute") && s.includes("linear-gradient"))
            div.remove();
        });
        doc.querySelectorAll<HTMLElement>("div").forEach((div) => {
          if ((div.getAttribute("class") ?? "").includes("banner-text"))
            div.remove();
        });
      }

      doc
        .querySelectorAll<HTMLElement>('[data-section="editable"]')
        .forEach((el) => {
          const fieldName = el.getAttribute("data-field") ?? "Message";
          const raw = emailFieldValues[fieldName];
          if (raw === undefined) return;
          const userHtml = resolveQuillAlignments(raw);
          const td = el.querySelector<HTMLElement>("td");
          if (td) {
            const originalTdHtml = td.innerHTML;
            td.innerHTML = originalTdHtml.includes("<table")
              ? reconstructEditableContent(userHtml, originalTdHtml)
              : userHtml;
          } else {
            el.innerHTML = quillHtmlToInner(userHtml, el.tagName);
          }
        });

      // Inject UTM short link into CTA button and fallback text link
      if (link) {
        doc.querySelectorAll<HTMLAnchorElement>("a").forEach((a) => {
          const text = a.textContent?.trim() ?? "";
          if (text.includes("View Reward Details")) {
            a.href = link;
          } else if (
            text.startsWith("http") &&
            a.closest("p")?.textContent?.includes("button above")
          ) {
            a.href = link;
            a.textContent = link;
          }
        });
      }

      return doc.documentElement.outerHTML;
    } catch {
      return null;
    }
  }, [
    rawEmailTemplate,
    emailFieldValues,
    selectedCampaignChannel,
    previewUrl,
    link,
  ]);

  const estimatedTotalCost = useMemo(() => {
    if (selectedCampaignChannel === ChannelEnum.Sms) {
      return estimatedTotalMember * cost * smsSegments;
    }
    return estimatedTotalMember * cost;
  }, [estimatedTotalMember, cost, selectedCampaignChannel, smsSegments]);

  // ======================================================
  // ⚛️ Effects
  // ======================================================

  useEffect(() => {
    if (campaignInfo?.Email) {
      fetchEVoucherList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignInfo]);

  // Only seed selectedEVouchers from the campaign's saved SKUs once. Without
  // this guard, campaignInfo re-rendering with a new reference re-triggers
  // fetchEVoucherList (see the effect above), which gives listEVoucher a new
  // array identity and re-runs this effect — silently reinstating the saved
  // bundles right after the user clicks the delete icon to clear them, so
  // the delete only "sticks" on a second click once refetching has settled.
  const eVoucherSyncedRef = useRef(false);
  useEffect(() => {
    if (eVoucherSyncedRef.current) return;
    const skus = getCampaignEVoucherSkus(data);
    if (!skus.length || !listEVoucher.length) return;
    const skuSet = new Set(skus);
    const matches = listEVoucher.filter((v) => skuSet.has(v.SKU));
    if (matches.length) {
      setSelectedEVouchers(matches);
      eVoucherSyncedRef.current = true;
    }
  }, [data, listEVoucher]);

  // Campaign has e-voucher SKU(s) saved, but none matched the fetched
  // e-voucher list — either the SKU isn't in the list, or the list itself
  // failed/came back empty. Falls back to showing just the selected count
  // instead of an empty picker.
  const eVoucherNotFoundCount = useMemo(() => {
    if (eVoucherFallbackCleared || selectedEVouchers.length > 0) return 0;
    return getCampaignEVoucherSkus(data).length;
  }, [data, selectedEVouchers, eVoucherFallbackCleared]);

  useEffect(() => {
    if (nameError === null) {
      form.validateFields(["Name"]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameError]);

  useEffect(() => {
    if (SHOW_REWARD_TYPES.includes(selectedType))
      form.validateFields(["Voucher"]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVoucher, selectedEVouchers, eVoucherNotFoundCount]);

  useEffect(() => {
    if (!selectedVoucher) return;
    if (isMessageUserEdited.current) {
      const currentMsg = form.getFieldValue("Message");
      if (!currentMsg) return;
      const resolved = resolveVoucherVars(currentMsg);
      if (resolved !== currentMsg) form.setFieldsValue({ Message: resolved });
      return;
    }
    if (!selectedType || !selectedCampaignChannel) return;
    if (selectedCampaignChannel === ChannelEnum.WhatsAppPremium) return;
    const defaultMsg = getDefaultMessage(
      selectedType,
      selectedCampaignChannel,
      selectedVoucher?.RedemptionDateBasedOn,
      selectedEVouchers,
      incompleteProfileFieldsValue,
    );
    if (defaultMsg)
      form.setFieldsValue({ Message: resolveVoucherVars(defaultMsg) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVoucher]);

  useEffect(() => {
    if (!selectedType || !selectedCampaignChannel) return;
    if (selectedCampaignChannel === ChannelEnum.WhatsAppPremium) return;
    if (isMessageUserEdited.current) return;
    const defaultMsg = getDefaultMessage(
      selectedType,
      selectedCampaignChannel,
      selectedVoucher?.RedemptionDateBasedOn,
      selectedEVouchers,
      incompleteProfileFieldsValue,
    );
    if (defaultMsg)
      form.setFieldsValue({
        Message: resolveEVoucherVars(resolveVoucherVars(defaultMsg)),
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType]);

  useEffect(() => {
    if (selectedEVouchers.length === 0) return;
    if (isMessageUserEdited.current) {
      const currentMsg = form.getFieldValue("Message");
      if (!currentMsg) return;
      const resolved = resolveEVoucherVars(currentMsg);
      if (resolved !== currentMsg) form.setFieldsValue({ Message: resolved });
      return;
    }
    if (!selectedType || !selectedCampaignChannel) return;
    if (selectedCampaignChannel === ChannelEnum.WhatsAppPremium) return;
    const defaultMsg = getDefaultMessage(
      selectedType,
      selectedCampaignChannel,
      selectedVoucher?.RedemptionDateBasedOn,
      selectedEVouchers,
      incompleteProfileFieldsValue,
    );
    if (defaultMsg)
      form.setFieldsValue({
        Message: resolveEVoucherVars(resolveVoucherVars(defaultMsg)),
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEVouchers]);

  useEffect(() => {
    if (!isProfileCompletionReminder) return;
    if (isMessageUserEdited.current) return;
    if (!selectedType || !selectedCampaignChannel) return;
    if (selectedCampaignChannel === ChannelEnum.WhatsAppPremium) return;
    const defaultMsg = getDefaultMessage(
      selectedType,
      selectedCampaignChannel,
      selectedVoucher?.RedemptionDateBasedOn,
      selectedEVouchers,
      incompleteProfileFieldsValue,
    );
    if (defaultMsg) form.setFieldsValue({ Message: defaultMsg });
    // Same reasoning as the effects above — intentionally scoped to
    // incompleteProfileFieldsValue only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incompleteProfileFieldsValue]);

  useEffect(() => {
    if (brandId && !campaignInfo) {
      campaignService.getCampaignInfo(brandId).then((info) => {
        if (info) setCampaignInfo(info);
      });
    }

    const timeout = setTimeout(async () => {
      if (brandId) {
        await fetchListAccountBrand();
        await fetchBrandMemberSegment();
        await fetchBrandRFMProfile();
        await fetchPostalDistrict();
        await fetchBlockDate();
        await fetchVoucherList();
        await fetchLinkOS();
        // await fetchMessageTemplates();
      }
    }, 100);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, selectedCampaignChannel]);

  useEffect(() => {
    if (!data) return;

    if (data?.CampaignStart && data?.Status !== StatusEnum.Draft) {
      const startDate = new Date(data.CampaignStart);
      const oneHourBefore = new Date(startDate.getTime() - 60 * 60 * 1000);
      const now = new Date();
      if (now > oneHourBefore) {
        setShowTimeout(true);
      }
    }
    if (data?.CampaignRunLog?.some((log) => log.IsBilled)) {
      setShowTimeout(true);
    }
  }, [data]);

  useEffect(() => {
    if (selectedCampaignChannel === ChannelEnum.WhatsAppPremium) {
      form.setFieldsValue({
        Message:
          whatsappTextToHtml(selectedMessageTemplates?.Content ?? "") ?? "",
      });
      form.validateFields(["Message"]);
    }
  }, [selectedCampaignChannel, selectedMessageTemplates, form]);

  // ======================================================
  // 📤 Upload Handling
  // ======================================================

  // ======================================================
  // 🚫 Exclude Targeted Member (blacklist upload)
  // ======================================================
  const [excludeFileList, setExcludeFileList] = useState<UploadFile[]>([]);
  const [excludeRows, setExcludeRows] = useState<Record<string, any>[]>([]);
  const [excludeMembers, setExcludeMembers] = useState<ExcludeMember[]>([]);
  const [excludeRawFile, setExcludeRawFile] = useState<File | null>(null);
  const [excludeParsing, setExcludeParsing] = useState(false);
  const [isExcludePreviewOpen, setIsExcludePreviewOpen] = useState(false);
  const [originalHadExcludeMembers, setOriginalHadExcludeMembers] =
    useState(false);

  // ======================================================
  // 🎯 Uploaded Member List (fixed target upload — bypasses RFM)
  // ======================================================
  const [targetFileList, setTargetFileList] = useState<UploadFile[]>([]);
  const [targetRows, setTargetRows] = useState<Record<string, any>[]>([]);
  const [targetMembers, setTargetMembers] = useState<TargetMember[]>([]);
  const [targetRawFile, setTargetRawFile] = useState<File | null>(null);
  const [targetParsing, setTargetParsing] = useState(false);
  const [isTargetPreviewOpen, setIsTargetPreviewOpen] = useState(false);
  const [originalHadTargetMembers, setOriginalHadTargetMembers] =
    useState(false);
  const targetPreviewRows = useMemo(() => {
    if (selectedTemplate !== TemplatesTypeEnum.UploadedMemberList) {
      return targetRows;
    }
    // Loaded from the backend, which already persists Found/Not Found per
    // member — trust that over re-deriving it ourselves.
    if (targetRows.length && "Status" in targetRows[0]) {
      return targetRows;
    }
    // Freshly uploaded, not-yet-persisted file — best-effort match against
    // the estimate check's result.
    return alreadyReload
      ? annotateTargetMemberMatchStatus(targetRows, estimatedMember)
      : targetRows;
  }, [targetRows, estimatedMember, selectedTemplate, alreadyReload]);
  const targetFoundCount =
    selectedTemplate === TemplatesTypeEnum.UploadedMemberList &&
    targetPreviewRows.length > 0 &&
    "Status" in targetPreviewRows[0]
      ? targetPreviewRows.filter((r) => r.Status === TARGET_MEMBER_FOUND).length
      : undefined;

  const readyToReload =
    selectedTemplate === TemplatesTypeEnum.UploadedMemberList
      ? targetMembers.length > 0
      : isProfileCompletionReminder
        ? true
        : hasCriteria && hasPeriod && hasSegment;

  const openNotificationWithIcon = () => {
    notif.error({
      message: "Error!",
      description:
        "An error occurred while uploading the image. Please try again with a different image or contact the administrator.",
      placement: "top",
    });
  };
  const uploadToS3 = async (file: File) => {
    let simInterval: number | undefined;
    let finished = false;
    const start = Date.now();

    const startSimulatedProgress = () => {
      setProgressUpload(1);
      simInterval = window.setInterval(() => {
        setProgressUpload((prev) => {
          if (finished) return prev;
          if (prev >= 75) return 75;
          const next = prev + 2;
          return next >= 75 ? 75 : next;
        });
        return;
      }, 100);
    };

    const clearSim = () => {
      if (simInterval) {
        window.clearInterval(simInterval);
        simInterval = undefined;
      }
    };

    try {
      setIsUploading(true);
      setProgressUpload(0);
      startSimulatedProgress();

      const res = await campaignService.uploadCampaignImage(file);
      finished = true;
      clearSim();
      if (!res || !res.result || !res.result.Url) {
        openNotificationWithIcon();
      }
      const elapsed = Date.now() - start;
      const remain = elapsed < 1000 ? 1000 - elapsed : 0;
      if (remain > 0) {
        await new Promise((r) => setTimeout(r, remain));
      }

      setProgressUpload(100);

      const imageUrl = res?.result?.Url as string | undefined;
      if (!imageUrl) throw new Error("Upload succeeded but URL is missing.");

      form?.setFieldsValue?.({ Image: imageUrl });
      setPreviewUrl(imageUrl);

      setFileList((lst) =>
        lst.length
          ? [
              {
                ...lst[0],
                status: "done",
                url: imageUrl,
                response: { Url: imageUrl },
              },
            ]
          : lst,
      );

      message.success("Image uploaded.");
    } catch (err: any) {
      finished = true;
      clearSim();
      message.error(
        err?.response?.data?.message || err?.message || "Upload failed",
      );
      setFileList([]);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      form?.setFieldsValue?.({ Image: undefined });
    } finally {
      setTimeout(() => setProgressUpload(0), 300);
      setIsUploading(false);
    }
  };

  const handleChangeUpload = (info: UploadChangeParam<UploadFile<any>>) => {
    const latestList = info.fileList.slice(-1);
    setFileList(latestList);
    setUploadingFile(latestList[0] ?? null);
    if (latestList.length) setProgressUpload(0);
  };

  const beforeUpload = (file: File) => {
    const isValidType = ["image/jpeg", "image/png"].includes(file.type);
    const isLt5M = file.size / 1024 / 1024 <= 5;

    if (!isValidType) {
      message.error("Only JPG/PNG are allowed.");
      return Upload.LIST_IGNORE;
    }
    if (!isLt5M) {
      message.error("Max file size is 5 MB.");
      return Upload.LIST_IGNORE;
    }

    const url = URL.createObjectURL(file);

    const item: UploadFile = {
      uid: `${Date.now()}`,
      name: file.name,
      status: "uploading",
      originFileObj: file as any,
      url,
      thumbUrl: url,
    };
    setFileList([item]);

    uploadToS3(file);

    return false;
  };
  // ======================================================
  // 🔎 Search & Voucher Selection
  // ======================================================
  const handleRemove = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setFileList([]);
    setUploadingFile(null);
    setProgressUpload(0);
  };

  // ======================================================
  // 🚫 Exclude Targeted Member Upload Handling
  // ======================================================
  const beforeUploadExclude = (file: File) => {
    const isExcel =
      file.name.toLowerCase().endsWith(".xlsx") ||
      file.name.toLowerCase().endsWith(".xls");
    if (!isExcel) {
      message.error("Only Excel files (.xlsx, .xls) are allowed.");
      return Upload.LIST_IGNORE;
    }
    const isLt5M = file.size / 1024 / 1024 <= 5;
    if (!isLt5M) {
      message.error("Max file size is 5 MB.");
      return Upload.LIST_IGNORE;
    }

    (async () => {
      try {
        setExcludeParsing(true);
        const { rows, members } = await parseExcludeMemberFile(file);
        if (!members.length) {
          message.error(
            "No members found in the uploaded file. Please use the example format.",
          );
          return;
        }
        setExcludeRows(rows);
        setExcludeMembers(members);
        setExcludeRawFile(file);
        setExcludeFileList([
          {
            uid: `${Date.now()}`,
            name: file.name,
            status: "done",
          },
        ]);
        onChangeRFM();
        message.success(`${members.length} members loaded to exclude.`);
      } catch (err) {
        console.error("Failed to parse exclude file:", err);
        message.error("Failed to read the Excel file. Please try again.");
      } finally {
        setExcludeParsing(false);
      }
    })();

    return false;
  };

  const handleRemoveExclude = () => {
    setExcludeFileList([]);
    setExcludeRows([]);
    setExcludeMembers([]);
    setExcludeRawFile(null);
    onChangeRFM();
  };

  // ======================================================
  // 🎯 Uploaded Member List Upload Handling
  // ======================================================
  const beforeUploadTarget = (file: File) => {
    const isExcel =
      file.name.toLowerCase().endsWith(".xlsx") ||
      file.name.toLowerCase().endsWith(".xls");
    if (!isExcel) {
      message.error("Only Excel files (.xlsx, .xls) are allowed.");
      return Upload.LIST_IGNORE;
    }
    const isLt5M = file.size / 1024 / 1024 <= 5;
    if (!isLt5M) {
      message.error("Max file size is 5 MB.");
      return Upload.LIST_IGNORE;
    }

    (async () => {
      let parsedRows: Record<string, any>[] = [];
      try {
        setTargetParsing(true);
        const { rows, members } = await parseExcludeMemberFile(file);
        if (!members.length) {
          message.error(
            "No members found in the uploaded file. Please use the example format.",
          );
          return;
        }
        parsedRows = rows;
        setTargetRows(rows);
        setTargetMembers(members);
        setTargetRawFile(file);
        setTargetFileList([
          {
            uid: `${Date.now()}`,
            name: file.name,
            status: "done",
          },
        ]);
        onChangeRFM();
        message.success(`${members.length} members loaded as campaign target.`);
      } catch (err) {
        console.error("Failed to parse target member file:", err);
        message.error("Failed to read the Excel file. Please try again.");
      } finally {
        setTargetParsing(false);
      }

      // Auto-check the uploaded list against registered members instead of
      // requiring a manual Reload click — reuses the Estimated Total Members
      // flow directly. Pass the freshly parsed rows directly (not just the
      // file) since `targetRows` state wouldn't be updated yet in this
      // closure — see handleRefreshMember's overrideTargetRows param.
      if (parsedRows.length > 0) {
        await handleRefreshMember(file, parsedRows);
      }
    })();

    return false;
  };

  const handleRemoveTarget = () => {
    setTargetFileList([]);
    setTargetRows([]);
    setTargetMembers([]);
    setTargetRawFile(null);
    onChangeRFM();
  };

  const summary = selectedVoucher
    ? `${selectedVoucher.Name} | ${selectedVoucher.DiscountLabel}`
    : selectedEVouchers.length > 0
      ? selectedEVouchers.map((v) => `${v.Name} | ${v.Price}`).join(", ")
      : "No rewards selected";

  const isEmpty = (v: any) => v === undefined || v === null || v === "";

  const normalizeText = (input: string) =>
    input
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<[^>]*>/g, "")
      .replace(/\r\n/g, "\n")
      .replace(/\n{2,}/g, "\n")
      .replace(/[ \t]+/g, " ")
      .trim();

  const fetchCampaignData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const result = await campaignService.getCampaignDetailsById(Number(id));
      if (!result) return;

      const { Campaign: data, Voucher: voucher } = result;

      if (
        data.Status === StatusEnum.Completed ||
        data.Status === StatusEnum.Cancelled
      ) {
        navigateWithQuery(RoutePaths.IFRAME_TARGET_MARKETING_HOME, "id", null);
        return;
      }

      if (voucher) {
        setSelectedVoucher(voucher);
      }
      setData(data);

      const formValues = mapCampaignToFormValues(data);
      // Extract states from mapping result or data
      const firstMessage = data.Messages?.[0];
      const channel = data?.Channel;
      const link = firstMessage?.Link;
      const templates = firstMessage?.CampaignTemplates;

      if (channel != null) setSelectedCampaignChannel(channel as ChannelEnum);
      if (link) setLink(link);
      if (templates) setSelectedMessageTemplates(templates);

      if (channel === ChannelEnum.Email && firstMessage?.Content) {
        setSavedEmailContent(firstMessage.Content);
      }

      if (data.TemplateType) {
        setSelectedTemplate(data.TemplateType);
        if (data.InactiveDays) setInactiveDays(data.InactiveDays);
      }
      if (data.ScheduleEndType) {
        setEndScheduleType(data.ScheduleEndType);
      }
      if (data.ScheduleType) {
        if (data.ScheduleType !== TypeEnum.Onetime) {
          setScheduleType(TypeEnum.Recurring_Weekly);
        } else {
          setScheduleType(TypeEnum.Onetime);
        }
      }

      const messageContent = firstMessage?.Content || "";
      const hasUnsubscribe =
        normalizeText(messageContent).includes("To Unsub:") &&
        normalizeText(messageContent).includes("https://getz.co/a/UNSUB");
      setEnableTrackingLink(hasUnsubscribe);

      setSelectedPurchaseBrand(formValues.PurchaseBrand ?? [AllBrand]);
      setScheduleType(formValues.ScheduleType ?? TypeEnum.Onetime);

      if (Array.isArray(data.ExcludeMembers) && data.ExcludeMembers.length) {
        const members = data.ExcludeMembers;
        setExcludeMembers(members);
        setExcludeRows(
          members.map((m: ExcludeMember) => ({
            "Customer Contact": m.CustomerContact ?? m.PhoneNumber ?? "",
            "Email Address": m.Email ?? "",
          })),
        );
        setExcludeFileList([
          { uid: "-1", name: "Excluded members", status: "done" },
        ]);
        setOriginalHadExcludeMembers(true);
      }

      if (Array.isArray(data.TargetMembers) && data.TargetMembers.length) {
        const members = data.TargetMembers;
        setTargetMembers(members);
        setTargetRows(
          members.map((m: TargetMember) => ({
            "Customer Contact": m.CustomerContact ?? m.PhoneNumber ?? "",
            "Email Address": m.Email ?? "",
            // Backend already knows whether this member matched a registered
            // account — use it directly instead of re-deriving via the
            // frontend heuristic (that's only needed for a freshly uploaded,
            // not-yet-persisted file).
            Status: m.Found ? TARGET_MEMBER_FOUND : TARGET_MEMBER_NOT_FOUND,
          })),
        );
        setTargetFileList([
          { uid: "-1", name: "Target member list", status: "done" },
        ]);
        setOriginalHadTargetMembers(true);
      }
      if (data.EnableCustomTrackingLink) setEvoucherCustom(true);

      if (typeof data.EstimatedTotalMembers === "number") {
        setEstimatedTotalMember(data.EstimatedTotalMembers);
        setAlreadyReload(true);
      }

      const postalCodes = formValues.PostalCode ?? [];
      setPostalFilter(postalCodes);

      if (data.ImageUrl) {
        setPreviewUrl(data.ImageUrl);
        setFileList([
          {
            uid: String(data.Id ?? Date.now()),
            name: "campaign-image",
            status: "done",
            url: data.ImageUrl,
            thumbUrl: data.ImageUrl,
          },
        ]);
      } else {
        setPreviewUrl(null);
        setFileList([]);
      }

      const segMap = new Map<number, number>();
      data.Segments?.forEach((s: any) => {
        if (s?.SegmentId != null && s?.Id != null)
          segMap.set(Number(s.SegmentId), Number(s.Id));
      });
      setSegmentLinkIdBySegmentId(segMap);

      const pdMap = new Map<number, number>();
      data.PostalDistricts?.forEach((p: any) => {
        if (p?.DistrictId != null && p?.Id != null)
          pdMap.set(Number(p.DistrictId), Number(p.Id));
      });
      setPostalLinkIdByDistrictId(pdMap);

      const msgId = firstMessage?.Id;
      setMessageId(msgId ? Number(msgId) : null);

      // Send Date & Time: use the campaign's start when present, otherwise
      // default to tomorrow 08:00 (a valid slot — today is disabled). The
      // visible DatePicker is driven by `campaignDate` state; we mirror it to
      // the form field so validation + getFieldValue keep working.
      const startDayjs =
        data.CampaignStart && dayjs(data.CampaignStart).isValid()
          ? dayjs(data.CampaignStart)
          : dayjs().add(1, "day").startOf("day").hour(8).minute(0).second(0);

      // Set Form Values ONCE
      form.setFieldsValue(formValues as any);
      form.setFieldValue("CampaignDate", startDayjs);
      setCampaignDate(startDayjs);

      isMessageUserEdited.current = true;
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // const getChannelCost = (channel: ChannelEnum | null): number => {
  //   switch (channel) {
  //     case ChannelEnum.WhatsAppStandard:
  //       return Number(process.env.REACT_APP_WHATSAPP_COST) || 0;

  //     case ChannelEnum.WhatsAppPremium:
  //       return Number(process.env.REACT_APP_WHATSAPP_PREMIUM_COST) || 0;

  //     case ChannelEnum.Sms:
  //       return Number(process.env.REACT_APP_SMS_COST) || 0;

  //     default:
  //       return 0;
  //   }
  // };

  useEffect(() => {
    eVoucherSyncedRef.current = false;
    fetchCampaignData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (selectedCampaignChannel !== ChannelEnum.Email || !data) return;
    const firstMessage = data.Messages?.[0];
    console.log("á");
    // Defer past child Form.Item registration effects so setFieldsValue lands
    // after the email-only fields are mounted and registered with the form store.
    const timer = setTimeout(() => {
      form.setFieldsValue({
        MessageSubject: firstMessage?.MessageSubject ?? undefined,
        MessagePreheader: firstMessage?.MessagePreheader ?? undefined,
      });
    }, 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaignChannel, data]);

  useEffect(() => {
    if (
      !selectedType ||
      !brandId ||
      selectedCampaignChannel !== ChannelEnum.Email
    ) {
      setParsedEmailTemplate(null);
      setRawEmailTemplate(null);
      return;
    }
    const isVoucherType =
      selectedType === CampaignTypeEnum.Voucher ||
      selectedType === CampaignTypeEnum.VoucherPoint;
    campaignService
      .getEmailPreview(
        selectedType,
        brandId,
        isVoucherType ? selectedVoucher?.Id : undefined,
      )
      .then((rawHtml) => {
        setRawEmailTemplate(rawHtml);
        setParsedEmailTemplate(
          rawHtml ? (parseEmailTemplate(rawHtml) ?? null) : null,
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType, selectedVoucher?.Id, brandId, selectedCampaignChannel]);

  useEffect(() => {
    if (!parsedEmailTemplate) {
      setEmailFieldValues({});
      return;
    }
    const initial: Record<string, string> = {};

    if (savedEmailContent && selectedCampaignChannel === ChannelEnum.Email) {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(savedEmailContent, "text/html");
        parsedEmailTemplate.forEach((block) => {
          if (block.type === "editable") {
            const selector =
              block.fieldName === "Message"
                ? '[data-section="editable"]:not([data-field])'
                : `[data-section="editable"][data-field="${block.fieldName}"]`;
            const el = doc.querySelector<HTMLElement>(selector);
            if (el) {
              const td = el.querySelector<HTMLElement>("td");
              // Mirror parseEmailTemplate: row-level editables flatten td content
              // so Quill receives <hr>-separated parts, not raw nested HTML.
              initial[block.fieldName] = td
                ? flattenEditableContent(td.innerHTML)
                : el.innerHTML;
            } else {
              initial[block.fieldName] = block.defaultContent;
            }
          } else if (block.type === "editable-group") {
            block.fields.forEach(({ fieldName, defaultContent }) => {
              const el = doc.querySelector<HTMLElement>(
                `[data-section="editable"][data-field="${fieldName}"]`,
              );
              initial[fieldName] = el ? el.innerHTML : defaultContent;
            });
          }
        });
      } catch {
        parsedEmailTemplate.forEach((block) => {
          if (block.type === "editable")
            initial[block.fieldName] = block.defaultContent;
          else if (block.type === "editable-group")
            block.fields.forEach(
              ({ fieldName, defaultContent }) =>
                (initial[fieldName] = defaultContent),
            );
        });
      }
    } else {
      parsedEmailTemplate.forEach((block) => {
        if (block.type === "editable")
          initial[block.fieldName] = block.defaultContent;
        else if (block.type === "editable-group")
          block.fields.forEach(
            ({ fieldName, defaultContent }) =>
              (initial[fieldName] = defaultContent),
          );
      });
    }

    setEmailFieldValues(initial);
    const firstEditable = parsedEmailTemplate.find(
      (b) => b.type === "editable",
    );
    if (firstEditable && firstEditable.type === "editable") {
      form.setFieldsValue({
        Message:
          initial[firstEditable.fieldName] ?? firstEditable.defaultContent,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedEmailTemplate]);

  const SHOW_REWARD_TYPES = [
    CampaignTypeEnum.Voucher,
    CampaignTypeEnum.VoucherPoint,
    CampaignTypeEnum.EVoucher,
  ];

  const handleChangePurchaseBrand = (values: string[]) => {
    onChangeRFM();
    if (
      values.includes(AllBrand) &&
      !selectedPurchaseBrand.includes(AllBrand)
    ) {
      setSelectedPurchaseBrand([AllBrand, ...listPurchaseBrandFilter]);
      return;
    }

    if (
      !values.includes(AllBrand) &&
      selectedPurchaseBrand.includes(AllBrand)
    ) {
      setSelectedPurchaseBrand([]);
      return;
    }

    const allSelected = listPurchaseBrandFilter.every((b) =>
      values.includes(b),
    );
    if (allSelected) {
      setSelectedPurchaseBrand([AllBrand, ...listPurchaseBrandFilter]);
      return;
    }
    setSelectedPurchaseBrand(values.filter((v) => v !== AllBrand));
  };

  useEffect(() => {
    if (!listPurchaseBrandFilter?.length) return;

    if (
      selectedPurchaseBrand.includes(AllBrand) &&
      selectedPurchaseBrand.length === 1
    ) {
      const updated = [AllBrand, ...listPurchaseBrandFilter];
      setSelectedPurchaseBrand(updated);
      form.setFieldsValue({ PurchaseBrand: updated });
    } else {
      form.setFieldsValue({ PurchaseBrand: selectedPurchaseBrand });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPurchaseBrand, listPurchaseBrandFilter]);

  useEffect(() => {
    if (
      selectedTemplate === TemplatesTypeEnum.Default ||
      selectedTemplate === TemplatesTypeEnum.UploadedMemberList
    ) {
      form.setFieldsValue({
        Period: RFMPeriod.Lifetime,
        Segments: allSegmentIds,
        Profile: activeProfileId,
      });
    }
  }, [selectedTemplate, ProfileOptions, allSegmentIds, form, activeProfileId]);

  const handleError = ({ errorFields }: any) => {
    if (errorFields && errorFields.length > 0) {
      const firstName = Array.isArray(errorFields[0].name)
        ? errorFields[0].name[0]
        : errorFields[0].name;
      if (firstName === "CampaignDate") {
        campaignDateRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else {
        form.scrollToField(errorFields[0].name, {
          behavior: "smooth",
          block: "center",
        });
      }
      message.error(errorFields[0].errors?.[0]);
    }
  };

  const resolveVoucherVars = (message: string): string => {
    if (!selectedVoucher || !message) return message;
    const name = selectedVoucher.Name ?? "";
    const expiry =
      selectedVoucher.EndDateFormated || selectedVoucher.IssuanceEndDate
        ? dayjs(
            selectedVoucher.EndDateFormated ?? selectedVoucher.IssuanceEndDate,
          ).format("DD MMM YYYY")
        : "";
    const tagsArray: string[] = Array.isArray(
      selectedVoucher.RedemptionOrderTagsDisplayName,
    )
      ? selectedVoucher.RedemptionOrderTagsDisplayName
      : [];
    const tags =
      selectedCampaignChannel === ChannelEnum.Sms
        ? tagsArray.map((t, i) => `${i + 1}. ${t}`).join("\n")
        : `<ol>${tagsArray.map((t) => `<li>${t}</li>`).join("")}</ol>`;
    return message
      .replace(/\{VoucherName\}/g, name)
      .replace(/\{VoucherExpiry\}/g, expiry)
      .replace(
        /\{VoucherValidDays\}/g,
        selectedVoucher?.RedemptionDateBasedOnInterval?.toString() ?? "",
      )
      .replace(/\{RedemptionOrderTagsDisplayName\}/g, tags);
  };

  const resolveEVoucherVars = (message: string): string => {
    if (!message) return message;
    // Message vars only preview a single bundle's name/price — a campaign can now have
    // several, so the first selected bundle is used as the representative preview.
    const primaryEVoucher = selectedEVouchers[0];
    return message
      .replace(/\{EVoucherName\}/g, primaryEVoucher?.Name ?? "")
      .replace(/\{EVoucherPrice\}/g, primaryEVoucher?.Price?.toString() ?? "");
  };

  const applyDefaultMessage = () => {
    if (!selectedType || !selectedCampaignChannel) return;
    if (selectedCampaignChannel === ChannelEnum.WhatsAppPremium) return;

    if (selectedCampaignChannel === ChannelEnum.Email) {
      if (!parsedEmailTemplate) return;
      const initial: Record<string, string> = {};
      parsedEmailTemplate.forEach((block) => {
        if (block.type === "editable")
          initial[block.fieldName] = block.defaultContent;
        else if (block.type === "editable-group")
          block.fields.forEach(
            ({ fieldName, defaultContent }) =>
              (initial[fieldName] = defaultContent),
          );
      });
      setEmailFieldValues(initial);
      isMessageUserEdited.current = false;
      const firstEditable = parsedEmailTemplate.find(
        (b) => b.type === "editable",
      );
      if (firstEditable && firstEditable.type === "editable") {
        form.setFieldsValue({ Message: firstEditable.defaultContent });
      }
      return;
    }

    const defaultMsg = getDefaultMessage(
      selectedType,
      selectedCampaignChannel,
      selectedVoucher?.RedemptionDateBasedOn,
      selectedEVouchers,
      incompleteProfileFieldsValue,
    );
    if (!defaultMsg) return;
    isMessageUserEdited.current = false;
    let resolved = defaultMsg;
    if (selectedType === CampaignTypeEnum.EVoucher) {
      resolved = resolveEVoucherVars(resolved);
    } else if (
      selectedType === CampaignTypeEnum.Voucher ||
      selectedType === CampaignTypeEnum.VoucherPoint
    ) {
      resolved = resolveVoucherVars(resolved);
    }
    form.setFieldsValue({ Message: resolved });
  };

  // Note: unlike CreateCampaign.tsx, there's deliberately no auto-apply-a-
  // default-image-by-type effect here. On Update, a blank image means the
  // saved campaign has no image — that's the user's actual choice and
  // should stay blank, not get silently replaced by a default.

  const onPaymentLater = async () => {
    setIsBillingModalOpen(false);
    setLoading(true);
    try {
      const result = await campaignService.updateCampaignDetails(
        pendingPayload!,
        excludeRawFile ?? undefined,
        targetRawFile ?? undefined,
      );
      if (!result || result.status !== 1) {
        setShowTimeout(true);
        return;
      }
      navigateWithQuery(RoutePaths.IFRAME_DETAIL_CAMPAIGN, "id", id);
    } catch {
      setShowTimeout(true);
    } finally {
      setLoading(false);
    }
  };

  const weekdayOptions = Object.entries(WeekDay)
    .filter(([, value]) => typeof value === "number")
    .map(([key, value]) => ({
      label: key,
      value: value as number,
    }));

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      onFinishFailed={handleError}
      initialValues={{
        ScheduleType: TypeEnum.Onetime,
        Period: RFMPeriod.Lifetime,
        EndSchedule: EndScheduleEnum.OnSpecificDate,
        Frequency: TypeEnum.Recurring_Weekly,
      }}>
      {contextHolder}

      <Card
        title={
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={5} style={{ margin: 0 }}>
                Update{" "}
                {selectedCampaignChannel
                  ? ChannelKeyMap[selectedCampaignChannel]
                  : ""}{" "}
                Campaign
              </Title>
            </Col>
            <Col>
              <Button
                color="primary"
                variant="outlined"
                onClick={() =>
                  navigateWithQuery(
                    RoutePaths.IFRAME_TARGET_MARKETING_HOME,
                    "id",
                    null,
                  )
                }>
                Back
              </Button>
            </Col>
          </Row>
        }
        className="card-form-campaign">
        {/* {selectedCampaignChannel === ChannelEnum.WhatsAppStandard && (
          <Alert
            className=""
            type="info"
            description={
              <span>
                Campaign delivery is processed in batches, with a limit of 1,000
                recipients per day.
              </span>
            }
            showIcon
            style={{ marginBottom: 16 }}
          />
        )} */}

        {/* Campaign Details */}
        <Title level={5}>Campaign Details</Title>
        <Divider style={{ margin: "10px 0" }} />
        <Form.Item name="Status" hidden>
          <Input />
        </Form.Item>
        <FieldRow label="Campaign Name" required>
          <Form.Item
            name="Name"
            validateStatus={nameError ? "error" : ""}
            help={nameError}
            rules={[
              { required: true, message: "Please input campaign name" },
              {
                validator: (_, value) => {
                  if (!value) {
                    setNameError("Please input campaign name");
                  }
                  if (value && value.trim() === "") {
                    setNameError(
                      "Campaign name cannot be empty or spaces only",
                    );
                  }

                  const invalidCharRegex = /[^a-zA-Z0-9 _-]/;
                  if (invalidCharRegex.test(value)) {
                    setNameError(
                      "Campaign name cannot contain special characters like & * @ # etc.",
                    );
                    return Promise.reject(
                      "Campaign name cannot contain special characters like & * @ # etc.",
                    );
                  }
                  if (nameError) return Promise.reject(nameError);

                  return Promise.resolve();
                },
              },
            ]}
            style={{ marginBottom: 0 }}>
            <Input
              placeholder="Input Campaign Name"
              onChange={async (e) => {
                const value = e.target.value.trim();
                if (value) {
                  const isAvailable =
                    await FetchCheckAvailableCampaignName(value);
                  if (isAvailable) {
                    setNameError(null);
                    form.setFields([{ name: "Name", errors: [] }]);
                    if (shortenLinkTimerRef.current)
                      clearTimeout(shortenLinkTimerRef.current);
                    shortenLinkTimerRef.current = setTimeout(() => {
                      const generatedLink = `${campaignInfo?.MemberPortalUrl}?utm_source=${
                        selectedCampaignChannel
                          ? ChannelKeyMap[selectedCampaignChannel]
                          : ""
                      }&utm_campaign=${value}&utm_medium=MemberPortal`;
                      fetchShortenLink(generatedLink);
                    }, 800);
                  } else {
                    setNameError(
                      "A campaign with this name already exists. Please enter a unique campaign name",
                    );
                  }
                }
              }}
            />
          </Form.Item>
        </FieldRow>
        <FieldRow label="Campaign Type" required>
          <Form.Item
            name="Type"
            rules={[{ required: true, message: "Select Campaign Type" }]}
            style={{ marginBottom: 0 }}>
            <Select
              placeholder="Select Campaign Type"
              options={CAMPAIGN_TYPE_OPTIONS}
              allowClear
              onChange={(value) => {
                setSelectedEVouchers([]);
                setEVoucherFallbackCleared(true);
                setSelectedVoucher(null);
                setSelectedMessageTemplates(null);
                if (value !== CampaignTypeEnum.EVoucher && evoucherCustom) {
                  setEvoucherCustom(false);
                  const name = form.getFieldValue("Name")?.trim();
                  if (
                    name &&
                    campaignInfo?.MemberPortalUrl &&
                    selectedCampaignChannel
                  ) {
                    fetchShortenLink(
                      `${campaignInfo.MemberPortalUrl}?utm_source=${ChannelKeyMap[selectedCampaignChannel]}&utm_campaign=${name}&utm_medium=MemberPortal`,
                    );
                  }
                }
                if (value === CampaignTypeEnum.ProfileCompletionReminder) {
                  onChangeTemplate(TemplatesTypeEnum.Custom);
                  handleRemoveExclude();
                  setSelectedPurchaseBrand([AllBrand]);
                }
              }}
            />
          </Form.Item>
        </FieldRow>
        {!isProfileCompletionReminder && (
          <FieldRow label="Campaign Template Type" required>
            <Form.Item
              name="TemplateType"
              rules={[
                { required: true, message: "Select Campaign Template Type" },
              ]}
              style={{ marginBottom: 0 }}>
              <Select
                placeholder="Select Campaign Template Type"
                options={CAMPAIGN_TEMPLATES_OPTIONS}
                value={selectedTemplate}
                onChange={(value) => {
                  onChangeTemplate(value);
                }}
              />
              <div style={{ color: "#8c8c8c", marginTop: 6 }}>
                {selectedTemplate === TemplatesTypeEnum.Default
                  ? TEMPLATE_TYPE_DESCRIPTIONS[selectedTemplate].replace(
                      "{inactiveDays}",
                      String(inactiveDays),
                    )
                  : selectedTemplate
                    ? TEMPLATE_TYPE_DESCRIPTIONS[selectedTemplate]
                    : ""}
              </div>
            </Form.Item>
          </FieldRow>
        )}
        {isProfileCompletionReminder && (
          <FieldRow label="Incomplete Profile Fields" required>
            <Form.Item
              name="IncompleteProfileFields"
              rules={[{ required: true, message: "Select Fields to Complete" }]}
              style={{ marginBottom: 0 }}>
              <Select
                mode="multiple"
                placeholder="Select Fields to Complete"
                loading={!incompleteProfileFieldsReady}
                disabled={!incompleteProfileFieldsReady}
                options={incompleteProfileFieldOptions.map((f) => ({
                  label: f.label,
                  value: f.value,
                }))}
                allowClear
                style={{ width: "100%" }}
                maxTagCount={0}
                maxTagPlaceholder={(omittedValues) => {
                  const total = incompleteProfileFieldOptions.length;
                  const selected = omittedValues?.length ?? 0;
                  if (selected === 0) return null;
                  if (selected === total) return "All Fields";
                  return `${selected}/${total} selected`;
                }}
              />
            </Form.Item>
            <div style={{ color: "#8c8c8c", marginTop: 6 }}>
              Members missing any of the selected fields will be targeted with a
              reminder to complete their profile.{" "}
              {describeDefaultIncompleteProfileFields(incompleteProfileFieldOptions)}
            </div>
          </FieldRow>
        )}
        {selectedTemplate === TemplatesTypeEnum.UploadedMemberList && (
          <FieldRow label="Target Member List" required>
            <Form.Item
              style={{ marginBottom: 0 }}
              validateStatus={totalMembersError ? "error" : ""}
              help={totalMembersError || undefined}>
              <TargetMemberUpload
                fileList={targetFileList}
                memberCount={targetMembers.length}
                parsing={targetParsing}
                checking={loadingRefreshMember}
                matchedCount={targetFoundCount}
                beforeUpload={beforeUploadTarget}
                onRemove={handleRemoveTarget}
                onPreview={() => setIsTargetPreviewOpen(true)}
              />
            </Form.Item>
          </FieldRow>
        )}
        {selectedTemplate === TemplatesTypeEnum.Default &&
          !isProfileCompletionReminder && (
            <FieldRow label="Inactive Period (Days)">
              <Form.Item
                name="InactiveDays"
                rules={[{ required: true, message: "Please input days" }]}
                style={{ marginBottom: 0 }}>
                <InputNumber
                  min={1}
                  value={inactiveDays}
                  onChange={(value) => {
                    setInactiveDays(value || 60);
                    onChangeRFM();
                  }}
                  placeholder="Inactive Days"
                  addonAfter="Days"
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <div style={{ color: "#8c8c8c", marginTop: 6 }}>
                Define the number of days since a members last transaction or
                visit to be considered inactive.
              </div>
            </FieldRow>
          )}
        {selectedTemplate !== TemplatesTypeEnum.UploadedMemberList &&
          !isProfileCompletionReminder && (
            <>
              <FieldRow label="Selected RFM Period" required>
                <Form.Item
                  name="Period"
                  rules={[
                    { required: true, message: "Please select RFM Period" },
                  ]}
                  style={{ marginBottom: 0 }}>
                  <Select
                    onChange={onChangeRFM}
                    placeholder="Select RFM Period"
                    options={RfmPeriod}
                    style={{ width: "100%" }}
                    disabled={selectedTemplate === TemplatesTypeEnum.Default}
                  />
                </Form.Item>
              </FieldRow>

              <FieldRow label="Target RFM Segment" required>
                <Form.Item
                  name="Segments"
                  rules={[
                    { required: true, message: "Select Target RFM Segment" },
                  ]}
                  style={{ marginBottom: 0 }}>
                  <Select
                    onChange={onChangeRFM}
                    mode="multiple"
                    placeholder="Select Target RFM Segment"
                    options={SegmentOptions}
                    allowClear
                    style={{ width: "100%" }}
                    maxTagCount={0}
                    maxTagPlaceholder={(omittedValues) => {
                      const total = SegmentOptions?.length ?? 0;
                      const selected = omittedValues?.length ?? 0;

                      if (selected === 0) return null;
                      if (selected === total) return "All Segments";

                      return `${selected}/${total} selected`;
                    }}
                    disabled={selectedTemplate === TemplatesTypeEnum.Default}
                  />
                </Form.Item>
              </FieldRow>
              <FieldRow label="RFM Criteria Profile" required>
                <Form.Item
                  style={{ marginBottom: 0 }}
                  name="Profile"
                  rules={[
                    {
                      required: true,
                      message: "Please select RFM Criteria Profile",
                    },
                  ]}>
                  <Select
                    onChange={onChangeRFM}
                    placeholder="Select RFM Criteria Profile"
                    options={ProfileOptions}
                    style={{ width: "100%" }}
                    disabled={selectedTemplate === TemplatesTypeEnum.Default}
                  />
                </Form.Item>
              </FieldRow>
              <FieldRow label="Target Demographics (age)">
                <Row gutter={[12, 12]}>
                  <Col xl={4} xs={10}>
                    <Form.Item
                      name={["ageRange", "from"]}
                      dependencies={[["ageRange", "to"]]}
                      rules={[
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const to = getFieldValue(["ageRange", "to"]);
                            if (isEmpty(value) && isEmpty(to))
                              return Promise.resolve();
                            if (isEmpty(value) && !isEmpty(to)) {
                              return Promise.reject(
                                new Error("Please input minimum age"),
                              );
                            }
                            return Promise.resolve();
                          },
                        }),
                      ]}
                      style={{ marginBottom: 0 }}>
                      <InputNumber
                        min={0}
                        onChange={onChangeRFM}
                        placeholder="From Age"
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col
                    span={1}
                    style={{ textAlign: "center", lineHeight: "32px" }}>
                    <Divider
                      style={{
                        margin: "15px 0",
                        borderBlockStart: "1px solid black",
                      }}
                    />
                  </Col>
                  <Col xl={4} xs={10}>
                    <Form.Item
                      name={["ageRange", "to"]}
                      dependencies={[["ageRange", "from"]]}
                      rules={[
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const from = getFieldValue(["ageRange", "from"]);
                            if (isEmpty(value) && isEmpty(from))
                              return Promise.resolve();
                            if (!isEmpty(from) && isEmpty(value)) {
                              return Promise.reject(
                                new Error("Please input maximum age"),
                              );
                            }
                            if (!isEmpty(from) && !isEmpty(value)) {
                              const a = Number(from);
                              const b = Number(value);
                              if (!(b > a)) {
                                return Promise.reject(
                                  new Error(
                                    "To age must be higher than From age",
                                  ),
                                );
                              }
                            }
                            return Promise.resolve();
                          },
                        }),
                      ]}
                      style={{ marginBottom: 0 }}>
                      <InputNumber
                        min={0}
                        placeholder="To Age"
                        style={{ width: "100%" }}
                        onChange={onChangeRFM}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <div style={{ color: "#8c8c8c", marginTop: 6 }}>
                    Specify the age range of members to include in the campaign.
                  </div>
                </Row>
              </FieldRow>
              <FieldRow label="Target Geographic (Postal Code)">
                <Form.Item style={{ marginBottom: 0 }} name="PostalCode">
                  <Select
                    mode="multiple"
                    placeholder="Select Postal Code"
                    options={PostalCodeOptions}
                    style={{ width: "100%" }}
                    onChange={(value: any) => {
                      onChangeRFM();
                      setPostalFilter(value);
                    }}
                    showSearch={false}
                    filterOption={false}
                    maxTagCount={0}
                    maxTagPlaceholder={() => {
                      const selected: string[] =
                        form.getFieldValue("PostalCode") || [];
                      if (!selected.length) return null;
                      if (
                        selected.includes("all") ||
                        selected.length === postalTotal
                      ) {
                        return "All Postal Sectors";
                      }
                      return `${selected.length}/${postalTotal} selected`;
                    }}
                  />
                </Form.Item>
              </FieldRow>
              <FieldRow label="Purchase Brand" required>
                <Form.Item
                  style={{ marginBottom: 0 }}
                  name="PurchaseBrand"
                  rules={[{ required: true, message: "Please select Brand" }]}>
                  <Select
                    mode="multiple"
                    placeholder="Select Brands"
                    maxTagCount={0}
                    onChange={handleChangePurchaseBrand}
                    value={
                      form.getFieldValue("PurchaseBrand")?.includes(AllBrand)
                        ? [AllBrand, ...listPurchaseBrandFilter]
                        : form.getFieldValue("PurchaseBrand")
                    }
                    maxTagPlaceholder={() => {
                      const value = form.getFieldValue("PurchaseBrand") || [];
                      return value.includes(AllBrand)
                        ? AllBrand
                        : `${value.length}/${listPurchaseBrandFilter.length} selected`;
                    }}>
                    <Option key={AllBrand} value={AllBrand}>
                      {AllBrand}
                    </Option>
                    {listPurchaseBrandFilter.map((brand) => (
                      <Option key={brand} value={brand}>
                        {brand}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </FieldRow>
            </>
          )}
        {selectedTemplate !== TemplatesTypeEnum.UploadedMemberList &&
          !isProfileCompletionReminder && (
            <FieldRow label="Exclude Targeted Members (Optional)">
              <Form.Item style={{ marginBottom: 0 }}>
                <ExcludeMemberUpload
                  fileList={excludeFileList}
                  memberCount={excludeMembers.length}
                  parsing={excludeParsing}
                  beforeUpload={beforeUploadExclude}
                  onRemove={handleRemoveExclude}
                  onPreview={() => setIsExcludePreviewOpen(true)}
                />
              </Form.Item>
            </FieldRow>
          )}
        {selectedTemplate !== TemplatesTypeEnum.UploadedMemberList && (
          <FieldRow label="Estimated Total Members">
            <Form.Item
              validateStatus={totalMembersError ? "error" : ""}
              help={totalMembersError || undefined}
              style={{ marginBottom: 0 }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <InputNumber
                  style={{ width: isMobileOrTablet ? "70%" : "87%" }}
                  value={estimatedTotalMember}
                  addonAfter="Members"
                  disabled
                />
                <Button
                  disabled={!readyToReload}
                  color="primary"
                  variant="outlined"
                  loading={loadingRefreshMember}
                  onClick={() => handleRefreshMember()}
                  style={{
                    width: isMobileOrTablet ? "27%" : "12%",
                    marginLeft: "auto",
                  }}>
                  {loadingRefreshMember ? (
                    "Loading ..."
                  ) : (
                    <>
                      <SyncOutlined /> Reload
                    </>
                  )}
                </Button>
              </div>

              <div style={{ color: "#8c8c8c", marginTop: 6 }}>
                {changeRFM && (
                  <Alert
                    type="error"
                    showIcon
                    description="The displayed total member count may not be accurate. Please click Reload to ensure the latest data is reflected based on your current selections."
                  />
                )}

                {/* {!changeRFM && !alreadyReload && (
                <Alert
                  type="info"
                  showIcon
                  description="Estimation total number of members matched based on your selected RFM Period, Target Segment(s), RFM Criteria Profile, Target Demographics, and Target Geographic. Click Reload to view the result."
                />
              )} */}

                {firstReloadMember && !changeRFM ? (
                  <div style={{ fontSize: 13, color: "#595959" }}>
                    Estimation total number of members matched based on your
                    selected RFM Period, Target Segment(s), RFM Criteria
                    Profile, Target Demographics, and Target Geographic.
                  </div>
                ) : !changeRFM && alreadyReload && estimatedTotalMember > 0 ? (
                  <Alert
                    type="info"
                    showIcon
                    icon={
                      <img
                        src="/icon/icon-memberlist.svg"
                        alt="members"
                        style={{ width: 40, height: 40 }}
                      />
                    }
                    message={
                      <Row align="middle" justify="space-between">
                        <Col>
                          <div style={{ fontSize: 20, fontWeight: 600 }}>
                            {estimatedTotalMember}
                          </div>
                          <div style={{ fontSize: 13, color: "#595959" }}>
                            Estimation total members will receive this campaign
                          </div>
                        </Col>

                        <Col>
                          <Button
                            type="primary"
                            icon={<EyeOutlined />}
                            onClick={openPreview}>
                            Preview Members
                          </Button>
                        </Col>
                      </Row>
                    }
                  />
                ) : (
                  ""
                )}
              </div>
            </Form.Item>
          </FieldRow>
        )}

        <FieldRow label="Channel Cost per Message">
          <Space.Compact style={{ width: "100%" }}>
            <Input
              disabled
              value="$"
              style={{ width: 40, textAlign: "center" }}
            />
            <Form.Item name="CostPerMember" noStyle>
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={0.01}
                stringMode
                placeholder="0.00"
                disabled
              />
            </Form.Item>
          </Space.Compact>
          <div style={{ color: "#8c8c8c", marginTop: 6 }}>
            Cost to send one message, based on selected channel.
          </div>
        </FieldRow>

        <FieldRow label="Estimated Budget">
          <InputNumber
            style={{ width: "100%" }}
            value={
              estimatedTotalCost ? Number(estimatedTotalCost.toFixed(2)) : 0
            }
            disabled
          />
          <div style={{ color: "#8c8c8c", marginTop: 6 }}>
            Automatically calculated cost: Total Members X Channel Cost{" "}
            {selectedCampaignChannel === ChannelEnum.Sms && "X Total SMS"}
          </div>
        </FieldRow>

        <FieldRow label="Schedule Campaign" required>
          <Form.Item name="ScheduleType" noStyle rules={[{ required: true }]}>
            <Radio.Group onChange={onScheduleChange}>
              <Space direction="vertical">
                <Radio value={TypeEnum.Onetime}>
                  <Text style={{ fontWeight: "normal" }}>
                    One‑Time Campaign
                  </Text>
                  <div style={{ color: "grey", fontWeight: "normal" }}>
                    Send this campaign once at a specific date and time you
                    choose.
                  </div>
                </Radio>
                <Radio value={TypeEnum.Recurring_Weekly}>
                  <Text style={{ fontWeight: "normal" }}>
                    Recurring Campaign
                  </Text>
                  <div style={{ color: "grey", fontWeight: "normal" }}>
                    Send this campaign on a repeated schedule—weekly or monthly.
                  </div>
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        </FieldRow>
        {scheduleType === TypeEnum.Onetime &&
          selectedTemplate !== TemplatesTypeEnum.UploadedMemberList && (
            <FieldRow label="Target Members Mode" required>
              <Form.Item name="Mode" noStyle rules={[{ required: true }]}>
                <Radio.Group onChange={onModeChange} style={{ width: "100%" }}>
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Radio value={ModeEnum.Refresh}>
                      <Text style={{ fontWeight: "normal" }}>
                        Refresh target before sending
                      </Text>
                      <div style={{ color: "grey", fontWeight: "normal" }}>
                        Target members will be refreshed 1 day before send time.
                      </div>
                    </Radio>
                    <Alert
                      description={
                        <>
                          Target members may change before sending. This can
                          affect final reach and budget usage.
                        </>
                      }
                      type="warning"
                      showIcon
                    />
                    <Radio value={ModeEnum.Fixed}>
                      <Text style={{ fontWeight: "normal" }}>
                        Use fixed target
                      </Text>
                      <div
                        style={{
                          color: "grey",
                          fontWeight: "normal",
                        }}>
                        Target members remains the same as when the campaign was
                        created.{" "}
                      </div>
                    </Radio>
                  </Space>
                </Radio.Group>
              </Form.Item>
            </FieldRow>
          )}

        <div ref={campaignDateRef}>
          <FieldRow label="Send Date & Time" required>
            <Form.Item
              name="CampaignDate"
              style={{ marginBottom: 0 }}
              rules={[{ required: true, message: "Select date and time" }]}>
              <DatePicker
                value={campaignDate}
                showTime={{
                  format: "HH:mm",
                  minuteStep: 15,
                  showSecond: false,
                }}
                disabledDate={(current) => {
                  if (!current) return false;
                  return !current.isAfter(startOfToday, "day");
                }}
                disabledTime={(date) => {
                  if (!date) return {};
                  const base = buildDisabledTime(date, blocked);
                  const before8 = Array.from({ length: 8 }, (_, i) => i);
                  const baseHours = base.disabledHours();
                  return {
                    ...base,
                    disabledHours: () =>
                      Array.from(new Set([...baseHours, ...before8])).sort(
                        (a, b) => a - b,
                      ),
                  };
                }}
                format="YYYY-MM-DD HH:mm"
                style={{ width: "100%", height: "unset !important" }}
                placeholder="Select Send Date & Time"
                onChange={(date) => {
                  setCampaignDate(date);
                  form.setFieldValue("CampaignDate", date);
                }}
              />
              <div style={{ color: "#8c8c8c", marginTop: 6 }}>
                Campaigns must be scheduled at least 1 day in advance. Same-day
                scheduling is not available.
              </div>
            </Form.Item>
            {campaignDate && (
              <Alert
                description={
                  <>
                    {
                      <>
                        Your campaign will start at {formattedStart} and is
                        estimated to complete by {formattedEnd}.
                      </>
                    }
                  </>
                }
                type="info"
                showIcon
                style={{ marginTop: 10 }}
              />
            )}
          </FieldRow>
        </div>
        {scheduleType !== TypeEnum.Onetime && (
          <>
            <>
              <FieldRow label="Frequency" required>
                <Form.Item
                  name="Frequency"
                  rules={[{ required: true, message: "Select frequency" }]}
                  noStyle>
                  <Select
                    placeholder="Frequency"
                    style={{ width: "100%" }}
                    className="w-100"
                    defaultValue={TypeEnum.Recurring_Weekly}
                    options={[
                      { value: TypeEnum.Recurring_Weekly, label: "Weekly" },
                      { value: TypeEnum.Recurring_Monthly, label: "Monthly" },
                    ]}
                  />
                </Form.Item>
              </FieldRow>
            </>
          </>
        )}

        {frequencyValue === TypeEnum.Recurring_Weekly && (
          <FieldRow label="Repeat on Day(s)" required>
            <Form.Item
              name="RepeatDays"
              rules={[
                {
                  required: true,
                  message: "Please select at least one day.",
                },
              ]}
              style={{ marginBottom: 0 }}>
              <Select
                mode="multiple"
                options={weekdayOptions}
                placeholder="Select day(s) of the week"
                className="w-100"
                optionFilterProp="label"
                maxTagCount={0}
                maxTagPlaceholder={(selectedItems) =>
                  `${selectedItems.length} Selected Day(s)`
                }
              />
            </Form.Item>
          </FieldRow>
        )}

        {frequencyValue === TypeEnum.Recurring_Monthly && (
          <FieldRow label="Repeat Every" required>
            {" "}
            <Form.Item
              name="RepeatMonth"
              rules={[
                {
                  required: true,
                  message: "Please enter the repeat interval in months.",
                },
                {
                  type: "number",
                  min: 1,
                  message: "Repeat interval must be at least 1 month.",
                },
              ]}
              style={{ marginBottom: 0 }}>
              <InputNumber
                className="w-100"
                value={estimatedTotalMember}
                addonAfter="Month(s)"
                placeholder={"Input the repeat interval in months."}
                precision={0}
                step={1}
              />
            </Form.Item>
          </FieldRow>
        )}

        {scheduleType !== TypeEnum.Onetime && (
          <FieldRow label="End Schedule" required>
            <Form.Item
              name="ScheduleEndType"
              noStyle
              rules={[{ required: true }]}>
              <Radio.Group onChange={onEndScheduleChange}>
                <Space direction="vertical">
                  {/* <Radio value={EndScheduleEnum.Never}>
                    <Text style={{ fontWeight: "normal" }}>Never</Text>
                    <div style={{ color: "grey", fontWeight: "normal" }}>
                      This campaign will repeat indefinitely
                    </div>
                  </Radio> */}
                  <Radio value={EndScheduleEnum.OnSpecificDate}>
                    <Text style={{ fontWeight: "normal" }}>
                      {" "}
                      On specific date{" "}
                    </Text>
                    <div style={{ color: "grey", fontWeight: "normal" }}>
                      Ends on the selected date.{" "}
                    </div>
                  </Radio>
                  <Radio value={EndScheduleEnum.AfterOccurrences}>
                    <Text style={{ fontWeight: "normal" }}>
                      After X occurrences
                    </Text>
                    <div style={{ color: "grey", fontWeight: "normal" }}>
                      {" "}
                      Ends after the specified number of times.
                    </div>
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          </FieldRow>
        )}

        {scheduleType !== TypeEnum.Onetime &&
          endScheduleType === EndScheduleEnum.OnSpecificDate && (
            <FieldRow label="End Date & Time" required>
              <Form.Item
                name="EndDate"
                rules={[
                  {
                    required: true,
                    message: "Please select end date and time",
                  },
                ]}
                style={{ marginBottom: 0 }}>
                <DatePicker
                  showTime={{
                    format: "HH:mm",
                    minuteStep: 15,
                    showSecond: false,
                  }}
                  format="YYYY-MM-DD HH:mm"
                  style={{ width: "100%", height: "unset !important" }}
                  placeholder="Select End Date & Time"
                  disabledDate={(current) => {
                    if (!current) return false;
                    const campaignDate = form.getFieldValue("CampaignDate");

                    if (current.isBefore(startOfToday, "day")) {
                      return true;
                    }

                    if (campaignDate && !current.isAfter(campaignDate)) {
                      return true;
                    }

                    return false;
                  }}
                />
              </Form.Item>
            </FieldRow>
          )}

        {scheduleType !== TypeEnum.Onetime &&
          endScheduleType === EndScheduleEnum.AfterOccurrences && (
            <FieldRow label="Number of Occurrences" required>
              <Form.Item
                name="MaxOccurrences"
                rules={[
                  {
                    required: true,
                    message: "Please input number of occurrences",
                  },
                  {
                    type: "number",
                    min: 1,
                    message: "Must be at least 1",
                  },
                ]}
                style={{ marginBottom: 0 }}>
                <InputNumber
                  min={1}
                  style={{ width: "100%" }}
                  placeholder="Input number of occurrences"
                  addonAfter="Times"
                  precision={0}
                  step={1}
                />
              </Form.Item>
            </FieldRow>
          )}
        {scheduleType !== TypeEnum.Onetime && (
          <FieldRow label="Audience Re-targeting" required>
            <Form.Item
              name="Retargeting"
              noStyle
              rules={[
                { required: true, message: "Please select retargeting logic" },
              ]}>
              <Radio.Group style={{ width: "100%" }}>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Radio value={false}>
                    <Text style={{ fontWeight: "normal" }}>
                      Prevent repeated targeting
                    </Text>
                    <div style={{ color: "grey", fontWeight: "normal" }}>
                      {selectedType === CampaignTypeEnum.Voucher ||
                      selectedType === CampaignTypeEnum.VoucherPoint
                        ? "Member will only be targeted again after previous voucher expiry"
                        : "Each member can only receive this campaign once"}
                    </div>
                  </Radio>
                  <Radio value={true}>
                    <Text style={{ fontWeight: "normal" }}>
                      Allow repeated targeting
                    </Text>
                    <div style={{ color: "grey", fontWeight: "normal" }}>
                      Member can receive this recurring campaign multiple times.
                    </div>
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          </FieldRow>
        )}
        <FieldRow label="Extension Period (Days)" required>
          <Form.Item
            name="ExtensionDays"
            rules={[
              { required: true, message: "Please enter extension period" },
              {
                validator: (_, value) => {
                  if (value === undefined || value === null || value === "")
                    return Promise.resolve();
                  const n = Number(value);
                  if (n < minExtensionDays)
                    return Promise.reject(
                      new Error(
                        `Min ${minExtensionDays} days for this schedule type`,
                      ),
                    );
                  if (n > maxExtensionDays)
                    return Promise.reject(
                      new Error(
                        `Max ${maxExtensionDays} days for this schedule type`,
                      ),
                    );
                  return Promise.resolve();
                },
              },
            ]}
            style={{ marginBottom: 0 }}>
            <InputNumber
              min={minExtensionDays}
              max={maxExtensionDays}
              precision={0}
              step={1}
              style={{ width: "100%" }}
              addonAfter="Days"
              placeholder="Enter number of days"
            />
          </Form.Item>
          <div style={{ color: "#8c8c8c", marginTop: 6 }}>
            Extend the campaign performance calculation period after the
            campaign end date.
          </div>
        </FieldRow>
        <FieldRow
          label={
            scheduleType === TypeEnum.Onetime
              ? "Budget Amount Entire Campaign"
              : "Budget per Recurring Run"
          }
          required>
          <Space.Compact style={{ width: "100%" }}>
            <Form.Item
              name="Budget"
              validateTrigger={["onChange", "onBlur"]}
              rules={[
                {
                  required: true,
                  message: "Please input total campaign budget",
                },
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === "") {
                      return Promise.resolve();
                    }
                    return Number(value) >= cost
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(`Minimum required budget: ${cost}`),
                        );
                  },
                },
              ]}
              style={{ width: "100%", marginBottom: 0 }}>
              <InputNumber
                min={0}
                step={0.01}
                addonBefore="$"
                style={{ width: "100%" }}
                placeholder="Input Total Campaign Budget"
              />
            </Form.Item>
          </Space.Compact>
          <div style={{ color: "#8c8c8c", marginTop: 6 }}>
            Applies to this{" "}
            {scheduleType === TypeEnum.Onetime ? "one‑time" : "recurring"}{" "}
            campaign.
          </div>
        </FieldRow>

        <FieldRow label="Maximum Reach">
          <InputNumber
            style={{ width: "100%" }}
            value={maxReach}
            addonAfter="Members"
            formatter={(value) => {
              if (!value) return `0 / ${estimatedTotalMember}`;

              return `${Number(value)} / ${estimatedTotalMember}`;
            }}
            disabled
          />
          {maxReach > estimatedTotalMember ? (
            <Alert
              className="alert-info-budget-higher"
              type="info"
              showIcon
              style={{ padding: "8px 12px", marginTop: 10 }}
              description={
                <div style={{ fontSize: 14, lineHeight: 1.2 }}>
                  <div style={{ fontWeight: "bold", marginBottom: 4 }}>
                    Your budget is higher than required. Some budget may not be
                    used.
                  </div>
                  <div>
                    Adjust your budget or expand your targeting to utilize it
                    fully.
                  </div>
                </div>
              }
            />
          ) : maxReach < estimatedTotalMember ? (
            <Alert
              style={{ marginTop: 10 }}
              description={
                <>
                  Only part of your target members will be reached based on your
                  budget.
                </>
              }
              type="warning"
              showIcon
            />
          ) : (
            <div style={{ color: "#8c8c8c", marginTop: 10 }}>
              Recipients are selected from the top of the preview member list,
              up to the maximum number covered by your current budget.
            </div>
          )}
        </FieldRow>

        {/* <Row>
          <Col span={24}>
            <Alert
              message={<strong> Important Notice</strong>}
              description={
                <>
                  Estimation Total Members and Estimated Budget displayed are
                  estimates based on currently selected criteria. Actual member
                  count and budget used when the campaign runs may differ due to
                  real-time data changes, system conditions, or other factors.
                </>
              }
              type="warning"
              showIcon
            />
          </Col>
        </Row> */}

        {/* Message Content */}
        <Title level={5} style={{ marginBottom: 12 }}>
          Message Content
        </Title>

        <Divider />
        {SHOW_REWARD_TYPES.includes(selectedType) && (
          <FieldRow label="Rewards to Include" required>
            <Form.Item
              name="Voucher"
              style={{ width: "100%", marginBottom: 0 }}
              rules={[
                {
                  validator: () => {
                    if (
                      selectedVoucher ||
                      selectedEVouchers.length > 0 ||
                      eVoucherNotFoundCount > 0
                    ) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("Please choose rewards to include"),
                    );
                  },
                },
              ]}>
              {isEVoucher ? (
                selectedEVouchers.length > 0 ? (
                  <Space
                    direction="vertical"
                    style={{ width: "100%" }}
                    size={8}>
                    <Space.Compact style={{ width: "100%" }}>
                      <Input
                        readOnly
                        value={
                          selectedEVouchers.length === 1
                            ? `${selectedEVouchers[0].Name} | $${selectedEVouchers[0].Price}`
                            : `${selectedEVouchers.length} E-Vouchers Selected`
                        }
                        addonBefore={
                          <span style={{ color: "#8c8c8c" }}>
                            Rewards Selected:
                          </span>
                        }
                        prefix={
                          <CheckCircleFilled style={{ color: "#52c41a" }} />
                        }
                        suffix={
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteFilled />}
                            onClick={() => {
                              setSelectedEVouchers([]);
                              setEVoucherFallbackCleared(true);
                            }}
                          />
                        }
                        style={{
                          background: "#f5f5f5",
                          fontWeight: 600,
                          borderTopRightRadius: 0,
                          borderBottomRightRadius: 0,
                        }}
                      />
                      <Button
                        type="primary"
                        onClick={openVoucherModal}
                        style={{
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                        }}>
                        Change
                      </Button>
                    </Space.Compact>

                    {selectedEVouchers.length > 1 && (
                      <div
                        style={{
                          background: "#f0f5ff",
                          border: "1px solid #adc6ff",
                          borderRadius: 6,
                          padding: "8px 12px",
                        }}>
                        {(evoucherListExpanded
                          ? selectedEVouchers
                          : selectedEVouchers.slice(0, 2)
                        ).map((v) => (
                          <div key={v.Id}>
                            • {v.Name} | ${v.Price}
                          </div>
                        ))}
                        {selectedEVouchers.length > 2 && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={() =>
                              setEvoucherListExpanded((prev) => !prev)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                setEvoucherListExpanded((prev) => !prev);
                              }
                            }}
                            style={{ fontWeight: 600, cursor: "pointer" }}>
                            {evoucherListExpanded ? (
                              <>
                                Show Less <UpOutlined />
                              </>
                            ) : (
                              <>
                                Show {selectedEVouchers.length - 2} more{" "}
                                <DownOutlined />
                              </>
                            )}
                          </span>
                        )}
                      </div>
                    )}
                  </Space>
                ) : eVoucherNotFoundCount > 0 ? (
                  <Space.Compact style={{ width: "100%" }}>
                    <Input
                      readOnly
                      value={`${eVoucherNotFoundCount} E-Vouchers Selected`}
                      addonBefore={
                        <span style={{ color: "#8c8c8c" }}>
                          Rewards Selected:
                        </span>
                      }
                      prefix={
                        <CheckCircleFilled style={{ color: "#52c41a" }} />
                      }
                      suffix={
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteFilled />}
                          onClick={() => setEVoucherFallbackCleared(true)}
                        />
                      }
                      style={{
                        background: "#f5f5f5",
                        fontWeight: 600,
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                      }}
                    />
                    <Button
                      type="primary"
                      onClick={openVoucherModal}
                      style={{
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                      }}>
                      Change
                    </Button>
                  </Space.Compact>
                ) : (
                  <Row gutter={[12, 12]}>
                    <Col lg={4} xs={24}>
                      <Button
                        className="w-100"
                        onClick={openVoucherModal}
                        type="primary">
                        Select Rewards
                      </Button>
                    </Col>
                    <Col lg={20} xs={24}>
                      <Text type="secondary">No rewards selected</Text>
                    </Col>
                  </Row>
                )
              ) : selectedVoucher ? (
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    readOnly
                    value={summary}
                    addonBefore={
                      <span style={{ color: "#8c8c8c" }}>
                        Rewards Selected:
                      </span>
                    }
                    prefix={<CheckCircleFilled style={{ color: "#52c41a" }} />}
                    suffix={
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteFilled />}
                        onClick={() => {
                          setSelectedKey(null);
                          setSelectedVoucher(null);
                        }}
                      />
                    }
                    style={{
                      background: "#f5f5f5",
                      fontWeight: selectedRow ? 600 : 400,
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                    }}
                  />
                  <Button
                    type="primary"
                    onClick={openVoucherModal}
                    style={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    }}>
                    Change
                  </Button>
                </Space.Compact>
              ) : (
                <Row gutter={[12, 12]}>
                  <Col lg={4} xs={24}>
                    <Button
                      className="w-100"
                      onClick={openVoucherModal}
                      type="primary">
                      Select Rewards
                    </Button>
                  </Col>
                  <Col lg={20} xs={24}>
                    <Text type="secondary">No rewards selected</Text>
                  </Col>
                </Row>
              )}
            </Form.Item>
          </FieldRow>
        )}
        {(selectedCampaignChannel === ChannelEnum.WhatsAppStandard ||
          selectedCampaignChannel === ChannelEnum.WhatsAppPremium ||
          selectedCampaignChannel === ChannelEnum.Email) && (
          <FieldRow label="Campaign Image (Optional)">
            <Form.Item name="Image" style={{ width: "100%", marginBottom: 0 }}>
              <Row gutter={[12, 12]}>
                <Col lg={4} xs={24}>
                  <Upload
                    accept="image/png,image/jpeg"
                    listType="picture"
                    showUploadList={false}
                    beforeUpload={beforeUpload}
                    fileList={fileList}
                    maxCount={1}
                    className="w-100"
                    onChange={handleChangeUpload}>
                    <Button
                      className="w-100"
                      disabled={previewUrl ? true : false}>
                      Choose File
                    </Button>
                  </Upload>
                </Col>
                <Col lg={20} xs={24}>
                  {" "}
                  {fileList.length === 0 ? (
                    <Text type="secondary">
                      No file chosen <br />
                      Recommended Size: 1080×1080 (1:1) or 1920×1080 (16:9),
                      JPG/PNG, Max 5 MB.
                    </Text>
                  ) : isUploading ? (
                    <Progress
                      percent={progressUpload}
                      size="small"
                      status="active"
                    />
                  ) : (
                    <Space>
                      <Text>{fileList[0].name} </Text>
                      <Button
                        icon={<DeleteOutlined />}
                        type="text"
                        danger
                        onClick={handleRemove}>
                        Remove
                      </Button>
                    </Space>
                  )}
                </Col>
              </Row>
              <Row style={{ marginTop: 15, marginBottom: 15 }}>
                {previewUrl && (
                  <AntdImage
                    key={previewUrl}
                    src={previewUrl}
                    alt="Campaign preview"
                    width={250}
                    height={150}
                    style={{
                      objectFit: "contain",
                      borderRadius: 8,
                      border: "1px solid #f0f0f0",
                    }}
                    preview={{ src: previewUrl }}
                  />
                )}
              </Row>
            </Form.Item>
          </FieldRow>
        )}
        {/* <FieldRow label="Message Template">
          <Form.Item name="messageTemplate" noStyle>
            <Select
              placeholder="Select Message Template"
              options={MessageTemplates}
              allowClear
              style={{ width: "100%" }}
            />
          </Form.Item>
        </FieldRow> */}

        {selectedCampaignChannel === ChannelEnum.Email && (
          <>
            <FieldRow label="Email Subject">
              <Form.Item
                name="MessageSubject"
                rules={[
                  {
                    max: 50,
                    message: "Email Subject cannot exceed 50 characters",
                  },
                ]}>
                <Input
                  maxLength={50}
                  showCount
                  placeholder="Write subject line of the email"
                  onChange={() => {
                    isMessageUserEdited.current = true;
                  }}
                />
              </Form.Item>
            </FieldRow>

            <FieldRow label="Preheader">
              <Form.Item
                name="MessagePreheader"
                rules={[
                  {
                    max: 70,
                    message: "Preheader cannot exceed 70 characters",
                  },
                ]}>
                <Input
                  maxLength={70}
                  showCount
                  placeholder="Write short summary shown in inbox preview"
                  onChange={() => {
                    isMessageUserEdited.current = true;
                  }}
                />
              </Form.Item>
            </FieldRow>
          </>
        )}

        <FieldRow label="Message Content" required>
          {selectedCampaignChannel !== ChannelEnum.WhatsAppPremium &&
            selectedType > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                  marginBottom: 8,
                }}>
                {selectedCampaignChannel === ChannelEnum.Email && (
                  <Button
                    size="small"
                    onClick={handleOpenEmailPreview}
                    disabled={!selectedType || !brandId}>
                    Preview Email Template
                  </Button>
                )}
                <Button size="small" onClick={applyDefaultMessage}>
                  Use Default Message
                </Button>
              </div>
            )}
          <Form.Item
            name="Message"
            style={{ marginBottom: 0 }}
            rules={[
              {
                required: true,
                message: "Enter Message Content",
                validator: (_, value) => {
                  const text = value?.replace(/<[^>]*>/g, "").trim();
                  return text
                    ? Promise.resolve()
                    : Promise.reject("Enter Message Content");
                },
              },
            ]}>
            {selectedCampaignChannel === ChannelEnum.WhatsAppStandard && (
              <EditorQuill
                value={msg}
                onChange={(val) => {
                  isMessageUserEdited.current = true;
                  if (val === "<p><br></p>")
                    form.setFieldsValue({ Message: val });
                }}
              />
            )}
            {selectedCampaignChannel === ChannelEnum.Sms && (
              <Input.TextArea
                rows={4}
                placeholder="Enter Message Content"
                autoSize
                onChange={() => {
                  isMessageUserEdited.current = true;
                }}
              />
            )}
            {selectedCampaignChannel === ChannelEnum.Email &&
              (parsedEmailTemplate ? (
                <EmailTemplateEditor
                  template={parsedEmailTemplate}
                  imageUrl={previewUrl ?? undefined}
                  renderEditable={(fieldName, _defaultContent) => (
                    <EditorQuill
                      ref={(ref) => {
                        if (ref) emailQuillRefs.current.set(fieldName, ref);
                        else emailQuillRefs.current.delete(fieldName);
                      }}
                      value={emailFieldValues[fieldName] ?? _defaultContent}
                      onFocus={() => {
                        activeEmailFieldRef.current = fieldName;
                      }}
                      onChange={(val) => {
                        isMessageUserEdited.current = true;
                        setEmailFieldValues((prev) => ({
                          ...prev,
                          [fieldName]: val,
                        }));
                        form.setFieldsValue({ Message: val });
                      }}
                    />
                  )}
                />
              ) : (
                <EditorQuill
                  value={msg}
                  onChange={(val) => {
                    isMessageUserEdited.current = true;
                    form.setFieldsValue({ Message: val });
                  }}
                />
              ))}
            {selectedCampaignChannel === ChannelEnum.WhatsAppPremium &&
              (selectedMessageTemplates ? (
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    readOnly
                    value={templatesSummary}
                    addonBefore={
                      <span style={{ color: "#8c8c8c" }}>
                        Template Selected:
                      </span>
                    }
                    prefix={
                      selectedMessageTemplates ? (
                        <CheckCircleFilled style={{ color: "#52c41a" }} />
                      ) : (
                        <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
                      )
                    }
                    suffix={
                      selectedMessageTemplates ? (
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteFilled />}
                          onClick={() => {
                            setSelectedMessageTemplates(null);
                          }}
                        />
                      ) : null
                    }
                    style={{
                      background: "#f5f5f5",
                      fontWeight: selectedMessageTemplates ? 600 : 400,
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                    }}
                  />
                  <Button
                    type="primary"
                    onClick={() => setMessageTemplatesOpen(true)}
                    style={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    }}>
                    Change
                  </Button>
                </Space.Compact>
              ) : (
                <Row gutter={[12, 12]}>
                  <Col lg={4} xs={24}>
                    <Button
                      className="w-100"
                      onClick={() => setMessageTemplatesOpen(true)}
                      type="primary">
                      Select Template
                    </Button>
                  </Col>
                  <Col
                    lg={20}
                    xs={24}
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}>
                    <Text type="secondary">No template selected</Text>
                  </Col>
                </Row>
              ))}
          </Form.Item>
        </FieldRow>
        {selectedCampaignChannel !== ChannelEnum.WhatsAppPremium && (
          <FieldRow
            label={
              <span>
                Message Variables{" "}
                <Tooltip
                  title={
                    <>
                      Personalize your message with variables.
                      <br />
                      These will be replaced with real data when the campaign is
                      sent
                    </>
                  }>
                  <InfoCircleOutlined
                    style={{ color: "#8c8c8c", cursor: "pointer" }}
                  />
                </Tooltip>
              </span>
            }>
            {" "}
            <div style={{ marginTop: 8 }}>
              <div style={{ marginTop: 8 }}>
                <Space size={[8, 8]} wrap>
                  {filteredVars.map((v) => (
                    <Tag
                      key={v}
                      style={{ cursor: "pointer" }}
                      onClick={() => insertVar(v)}>
                      {v}
                    </Tag>
                  ))}
                </Space>
              </div>
            </div>
          </FieldRow>
        )}
        {/* <FieldRow
          label={
            <span>
              Default Message{" "}
              <Tooltip
                title={
                  <>
                    Personalize your message with variables.
                    <br />
                    These will be replaced with real data when the campaign is
                    sent
                  </>
                }
                placement="right"
              >
                <InfoCircleOutlined
                  style={{ color: "#8c8c8c", cursor: "pointer" }}
                />
              </Tooltip>
            </span>
          }
        >
          <Switch
            checked={enableTrackingLink}
            onChange={setEnableTrackingLink}
            checkedChildren="ON"
            unCheckedChildren="OFF"
          />
        </FieldRow> */}
        {link && selectedCampaignChannel !== ChannelEnum.WhatsAppStandard && (
          <FieldRow label={<span>Campaign Tracking Link (CTA)</span>}>
            <Form.Item
              name="Link"
              label=""
              rules={[
                { required: false },
                { type: "url", message: "Please enter a valid URL" },
              ]}>
              <Input
                prefix={<LinkOutlined />}
                placeholder="Enter a Call to Action Link"
                value={link}
                disabled={true}
              />
              <div style={{ color: "#8c8c8c", marginTop: 6 }}>
                This link is automatically generated for your campaign tracking.
                Allowing you to monitor customer engagement and clicks.{" "}
              </div>
            </Form.Item>
          </FieldRow>
        )}
        {isEVoucher &&
          (selectedCampaignChannel === ChannelEnum.Sms ||
            selectedCampaignChannel === ChannelEnum.WhatsAppStandard ||
            selectedCampaignChannel === ChannelEnum.WhatsAppPremium) && (
            <>
              <FieldRow
                label={
                  selectedCampaignChannel === ChannelEnum.WhatsAppStandard
                    ? "OK Auto-Reply Message"
                    : "Message CTA"
                }>
                <Radio.Group
                  value={evoucherCustom ? 2 : 1}
                  onChange={(e) => {
                    const isCustom = e.target.value === 2;
                    setEvoucherCustom(isCustom);
                    const name = form.getFieldValue("Name")?.trim();
                    if (
                      name &&
                      campaignInfo?.MemberPortalUrl &&
                      selectedCampaignChannel
                    ) {
                      if (isCustom) {
                        const customUrl = form
                          .getFieldValue("CustomUrl")
                          ?.trim();
                        fetchShortenLink(
                          `${campaignInfo.MemberPortalUrl}?utm_source=${ChannelKeyMap[selectedCampaignChannel]}&utm_campaign=${name}&utm_medium=MemberPortal${customUrl ? `&targetedUrl=${encodeURIComponent(customUrl)}` : ""}`,
                        );
                      } else {
                        fetchShortenLink(
                          `${campaignInfo.MemberPortalUrl}?utm_source=${ChannelKeyMap[selectedCampaignChannel]}&utm_campaign=${name}&utm_medium=MemberPortal`,
                        );
                      }
                    }
                  }}>
                  <Space direction="vertical">
                    <Radio value={1}>
                      <Text style={{ fontWeight: "normal" }}>
                        {selectedCampaignChannel ===
                        ChannelEnum.WhatsAppStandard
                          ? "Default Auto-Reply"
                          : "Use default CTA"}
                      </Text>
                      <div style={{ color: "grey", fontWeight: "normal" }}>
                        {selectedCampaignChannel ===
                        ChannelEnum.WhatsAppStandard
                          ? "Use the default auto-reply message and reward link sent after customers reply with “OK”."
                          : "Use the default CTA label and destination link."}
                      </div>
                    </Radio>
                    <Radio value={2}>
                      <Text style={{ fontWeight: "normal" }}>
                        {selectedCampaignChannel ===
                        ChannelEnum.WhatsAppStandard
                          ? "Customize Auto-Reply"
                          : "Customize CTA"}
                      </Text>
                      <div style={{ color: "grey", fontWeight: "normal" }}>
                        {selectedCampaignChannel ===
                        ChannelEnum.WhatsAppStandard
                          ? "Customize the auto-reply message and destination link sent after customers reply with “OK”."
                          : "Customize the CTA label and destination link."}{" "}
                      </div>
                    </Radio>
                  </Space>
                </Radio.Group>
              </FieldRow>
              {evoucherCustom && (
                <>
                  <FieldRow
                    label={
                      selectedCampaignChannel === ChannelEnum.WhatsAppStandard
                        ? "Reply CTA Text"
                        : "CTA Text"
                    }
                    required>
                    <Form.Item
                      name="CustomCopyText"
                      style={{ marginBottom: 0 }}
                      rules={[
                        { required: true, message: "Please input CTA Text" },
                      ]}>
                      <Input placeholder='e.g. "View here"' />
                    </Form.Item>
                    <div style={{ color: "#8c8c8c", marginTop: 6 }}>
                      {selectedCampaignChannel === ChannelEnum.WhatsAppStandard
                        ? "Displayed before the link in the WhatsApp auto-reply message."
                        : "This text will appear before the destination link in the message preview."}
                    </div>
                  </FieldRow>
                  <FieldRow
                    label={
                      selectedCampaignChannel === ChannelEnum.WhatsAppStandard
                        ? "Reply Destination Link"
                        : "CTA Destination Link"
                    }
                    required>
                    <Form.Item
                      name="CustomUrl"
                      style={{ marginBottom: 0 }}
                      rules={[
                        { type: "url", message: "Please enter a valid URL" },
                        {
                          required: true,
                          message: "Please enter CTA Destination Link",
                        },
                      ]}>
                      <Input
                        prefix={<LinkOutlined />}
                        placeholder="https://..."
                        onChange={(e) => {
                          const customUrl = e.target.value.trim();
                          const campaignName = form
                            .getFieldValue("Name")
                            ?.trim();
                          if (!customUrl || !campaignName) return;
                          if (shortenLinkTimerRef.current)
                            clearTimeout(shortenLinkTimerRef.current);
                          shortenLinkTimerRef.current = setTimeout(() => {
                            const generatedLink = `${campaignInfo?.MemberPortalUrl}?utm_source=${
                              selectedCampaignChannel
                                ? ChannelKeyMap[selectedCampaignChannel]
                                : ""
                            }&utm_campaign=${campaignName}&utm_medium=MemberPortal&targetedUrl=${encodeURIComponent(customUrl)}`;
                            fetchShortenLink(generatedLink);
                          }, 800);
                        }}
                      />
                    </Form.Item>
                    <div style={{ color: "#8c8c8c", marginTop: 6 }}>
                      For SMS: appended as tracking URL. For WhatsApp: replaces
                      the default member portal link in the auto-reply.
                    </div>
                  </FieldRow>
                </>
              )}
              {link && (
                <FieldRow label={<span>Campaign Tracking Link (CTA)</span>}>
                  <Form.Item
                    name="Link"
                    label=""
                    rules={[
                      { required: false },
                      { type: "url", message: "Please enter a valid URL" },
                    ]}>
                    <Input
                      prefix={<LinkOutlined />}
                      placeholder="Enter a Call to Action Link"
                      value={link}
                      disabled={true}
                    />
                    <div style={{ color: "#8c8c8c", marginTop: 6 }}>
                      This link is automatically generated for your campaign
                      tracking. Allowing you to monitor customer engagement and
                      clicks.{" "}
                    </div>
                  </Form.Item>
                </FieldRow>
              )}
            </>
          )}
        <FieldRow
          label={
            <span>
              Preview Message{" "}
              <Tooltip
                title={
                  <>
                    This preview message shows the content message
                    <br /> along with the Campaign Tracking Link (CTA),
                    <br /> While the Unsubscribe link displayed is only a
                    placeholder.
                  </>
                }
                placement="right">
                <InfoCircleOutlined
                  style={{ color: "#8c8c8c", cursor: "pointer" }}
                />
              </Tooltip>
            </span>
          }>
          {/* Preview */}
          <div style={{ marginTop: 16 }}>
            <Text strong>Live Preview</Text>
            <Card size="small" style={{ marginTop: 8 }}>
              <div style={{ color: "#8c8c8c" }}>
                {previewUrl &&
                  selectedCampaignChannel !== ChannelEnum.Email && (
                    <>
                      <AntdImage
                        key={previewUrl}
                        src={previewUrl}
                        alt="Campaign preview"
                        width={250}
                        height={150}
                        style={{
                          objectFit: "contain",
                          borderRadius: 8,
                          border: "1px solid #f0f0f0",
                        }}
                        preview={false}
                      />
                      <br />
                    </>
                  )}
                {selectedCampaignChannel !== ChannelEnum.Email && (
                  <div
                    className="preview-update-campaign"
                    dangerouslySetInnerHTML={{
                      __html: previewHtml,
                    }}
                  />
                )}
                {selectedCampaignChannel === ChannelEnum.Email &&
                  (composedEmailPreview ? (
                    <iframe
                      key={composedEmailPreview}
                      srcDoc={composedEmailPreview}
                      title="Email Live Preview"
                      style={{
                        width: "calc(100% + 24px)",
                        margin: "-12px",
                        border: "none",
                        display: "block",
                        minHeight: 400,
                      }}
                      onLoad={(e) => {
                        try {
                          const h =
                            e.currentTarget.contentDocument?.body?.scrollHeight;
                          if (h) e.currentTarget.style.height = `${h}px`;
                        } catch {}
                      }}
                    />
                  ) : (
                    <span>Select a campaign type to preview the email.</span>
                  ))}
                {selectedCampaignChannel === ChannelEnum.WhatsAppPremium && (
                  <Space
                    direction="vertical"
                    style={{ width: "100%", marginTop: 16 }}>
                    {selectedMessageTemplates?.TextLink1 && (
                      <Button
                        block
                        style={{
                          color: "#00A884",
                          fontWeight: 600,
                        }}
                        icon={
                          <img
                            src="/icon/icon-button-whatsapp.svg"
                            style={{ width: 16 }}
                            alt="icon-whatsapp"
                          />
                        }>
                        {selectedMessageTemplates?.TextLink1}
                      </Button>
                    )}

                    {selectedMessageTemplates?.TextLink2 && (
                      <Button
                        block
                        style={{
                          color: "#00A884",
                          fontWeight: 600,
                        }}
                        icon={
                          <img
                            src="/icon/icon-button-whatsapp.svg"
                            style={{ width: 16 }}
                            alt="Icon WhatsApp"
                          />
                        }>
                        {selectedMessageTemplates?.TextLink2}
                      </Button>
                    )}
                  </Space>
                )}
                {selectedCampaignChannel === ChannelEnum.WhatsAppStandard && (
                  <>
                    <br />
                    {isProfileCompletionReminder ? (
                      <>
                        Update your profile here:
                        <br />
                        👉 {effectiveCTAUrl}
                      </>
                    ) : (
                      <>
                        👉 Reply with <b>OK</b> to receive your membership login
                        link
                        <br />
                        👉 Reply with <b>STOP </b>to opt out of future WhatsApp
                        messages
                      </>
                    )}
                  </>
                )}{" "}
              </div>
            </Card>
            <Row justify="space-between" style={{ marginTop: 6 }}>
              <Col>
                <Text type="secondary">Characters used: {charCount}</Text>
              </Col>
              <Col>
                {selectedCampaignChannel === ChannelEnum.Sms && (
                  <Text type="secondary">Total SMS: {smsSegments}</Text>
                )}
              </Col>
            </Row>
          </div>
        </FieldRow>
        {/* OK Auto-Reply Preview — commented out
        {selectedCampaignChannel === ChannelEnum.WhatsAppStandard &&
          isEVoucher &&
          evoucherCustom && (
            <FieldRow label="OK Auto-Reply Preview">
              <Card size="small" style={{ marginTop: 8 }}>
                <div style={{ color: "#8c8c8c", whiteSpace: "pre-line" }}>
                  <div>
                    {evoucherCTAText || "Explore and purchase e-vouchers here:"}
                  </div>
                  <div>👉 {link || "{{memberPortalLink}}"}</div>
                </div>
              </Card>
            </FieldRow>
          )}
        */}
        {selectedCampaignChannel === ChannelEnum.Sms && (
          <Alert
            message={<strong> Important Notice</strong>}
            description={
              <>
                Some messages may use extra SMS when variables are applied. This
                can affect total recipients covered by your budget.
              </>
            }
            type="warning"
            showIcon
          />
        )}

        {/* Bottom actions */}
        {!isMobileOrTablet ? (
          <Row justify="end" gutter={8} style={{ marginTop: 16 }}>
            <Col></Col>
            <Col>
              <Button
                loading={loading || loadingRefreshMember}
                type="primary"
                size="large"
                onClick={() => {
                  form.submit();
                }}>
                Submit
              </Button>
            </Col>
          </Row>
        ) : (
          <Row justify="end" gutter={8} style={{ marginTop: 16 }}>
            <Col xs={12}></Col>
            <Col xs={12}>
              <Button
                className="w-100"
                type="primary"
                loading={loading || loadingRefreshMember}
                onClick={() => {
                  form.submit();
                }}>
                Submit
              </Button>
            </Col>
          </Row>
        )}
      </Card>
      <Modal
        title={
          selectedType === CampaignTypeEnum.EVoucher
            ? "Select E-Voucher(s)"
            : "Select Voucher(s)"
        }
        open={voucherOpen}
        onCancel={closeVoucherModal}
        footer={null}
        maskClosable={false}
        width={1200}>
        <Row justify={"space-between"} gutter={[8, 8]}>
          <Col xs={24} sm={12}>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder={`Search by ${
                selectedType === CampaignTypeEnum.EVoucher ? "E-" : ""
              }Voucher's name`}
              value={searchVoucher}
              onChange={(e) => setSearchVoucher(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={12} style={{ textAlign: "end" }}>
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                block
                style={{ flex: 1 }}
                icon={<SyncOutlined spin={loadingVoucher} />}
                onClick={() =>
                  selectedType === CampaignTypeEnum.EVoucher
                    ? fetchEVoucherList()
                    : fetchVoucherList()
                }>
                Refresh
              </Button>
              <a
                style={{ flex: 1 }}
                href={`${
                  selectedType === CampaignTypeEnum.EVoucher
                    ? process.env.REACT_APP_DASHBOARD_GETZPASS_URL
                    : process.env.REACT_APP_DASHBOARD_URL
                }${
                  selectedType === CampaignTypeEnum.EVoucher
                    ? "/Product#/e-voucher/create/0"
                    : "/voucher/create"
                }`}
                target="_blank"
                rel="noopener noreferrer">
                <Button block type="primary">
                  {" "}
                  <PlusOutlined />{" "}
                  {selectedType === CampaignTypeEnum.EVoucher
                    ? "Create E-Voucher"
                    : "Create Voucher"}
                </Button>
              </a>
            </div>
          </Col>
        </Row>

        <Space direction="vertical" style={{ width: "100%" }}>
          <VoucherTable
            selectedType={selectedType}
            voucherData={filteredVouchers}
            eVoucherData={filteredVEouchers}
            selectedKey={selectedKey}
            selectedKeys={
              selectedType === CampaignTypeEnum.EVoucher
                ? draftEVouchers.map((v) => v.Id)
                : undefined
            }
            onChangeSelected={(key, row) => {
              setSelectedKey(key);
              if (
                selectedType === CampaignTypeEnum.Voucher ||
                selectedType === CampaignTypeEnum.VoucherPoint
              ) {
                setSelectedVoucher(row);
              }
            }}
            onAddSelect={(row) => {
              if (
                selectedType === CampaignTypeEnum.Voucher ||
                selectedType === CampaignTypeEnum.VoucherPoint
              ) {
                setSelectedKey(row.Id);
                setSelectedVoucher(row);
                setVoucherOpen(false);
              }
            }}
            onSelectionChange={(_, rows) => setDraftEVouchers(rows)}
            loading={loadingVoucher}
          />
          {selectedType === CampaignTypeEnum.EVoucher && (
            <Row justify="space-between" align="middle">
              <Col>
                <Text type="secondary">
                  {draftEVouchers.length} E-Voucher(s) selected
                </Text>
              </Col>
              <Col>
                <Space>
                  <Button onClick={closeVoucherModal}>Cancel</Button>
                  <Button
                    type="primary"
                    onClick={() => {
                      setSelectedEVouchers(draftEVouchers);
                      setVoucherOpen(false);
                    }}>
                    Submit
                  </Button>
                </Space>
              </Col>
            </Row>
          )}
        </Space>
      </Modal>
      <BillingModal
        open={isBillingModalOpen}
        campaignName={campaignName}
        channel={selectedCampaignChannel}
        estimatedMembers={estimatedTotalMember}
        costPerMessage={cost}
        smsSegments={smsSegments}
        maxBudget={pendingPayload?.Budget}
        mode={pendingPayload?.Mode}
        campaignStart={pendingPayload?.CampaignStart}
        campaignEnd={pendingPayload?.CampaignEnd}
        confirmLabel="Submit Campaign"
        skipBilling={true}
        defaultAccount={data?.PaymentAccountEmail}
        onClose={() => setIsBillingModalOpen(false)}
        onPaymentLater={onPaymentLater}
        onConfirmPayment={async (email) => {
          setLoading(true);
          try {
            const result = await campaignService.updateCampaignDetails(
              { ...pendingPayload!, PaymentAccountEmail: email },
              excludeRawFile ?? undefined,
              targetRawFile ?? undefined,
            );
            if (!result || result.status !== 1) throw new Error();
          } finally {
            setLoading(false);
          }
        }}
        onSuccess={() => {
          setIsBillingModalOpen(false);
          setIsCampaignSuccessModalOpen(true);
        }}
        onFailed={() => {
          setIsBillingModalOpen(false);
          setIsCampaignFailedModalOpen(true);
        }}
        hidePayLater={true}
      />
      {/* Campaign Success Modal */}
      <Modal
        open={isCampaignSuccessModalOpen}
        onCancel={() => setIsCampaignSuccessModalOpen(false)}
        footer={null}
        centered
        width={520}
        closable={false}
        maskClosable={false}
        styles={{ body: { paddingTop: 28, paddingBottom: 24 } }}>
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%" }}
          size={16}>
          <CheckCircleFilled style={{ fontSize: 80, color: "#2ecc71" }} />
          <Title level={4} style={{ margin: 0, textAlign: "center" }}>
            Campaign Updated Successfully
          </Title>
          <Text type="secondary" style={{ textAlign: "center" }}>
            Your campaign <b>{campaignName}</b> has been updated successfully.
          </Text>
        </Space>
        <Divider style={{ margin: 5 }} />
        <Row>
          <Button
            type="primary"
            size="large"
            className="w-100"
            onClick={() => {
              setIsCampaignSuccessModalOpen(false);
              navigateWithQuery(RoutePaths.IFRAME_DETAIL_CAMPAIGN, "id", id);
            }}
            style={{ marginTop: 8 }}>
            View Details
          </Button>
        </Row>
      </Modal>
      {/* Campaign Failed Modal */}
      <Modal
        open={isCampaignFailedModalOpen}
        onCancel={() => setIsCampaignFailedModalOpen(false)}
        footer={null}
        centered
        width={520}
        closable={false}
        maskClosable={false}
        styles={{ body: { paddingTop: 28, paddingBottom: 24 } }}>
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%" }}
          size={16}>
          <ExclamationCircleFilled style={{ fontSize: 80, color: "#ff4d4f" }} />
          <Title level={4} style={{ margin: 0, textAlign: "center" }}>
            Campaign Update Failed
          </Title>
          <Text type="secondary" style={{ textAlign: "center" }}>
            Your campaign <b>{campaignName}</b> could not be updated. Please try
            again.
          </Text>
        </Space>
        <Divider style={{ margin: "16px 0 8px" }} />
        <Button
          size="large"
          className="w-100"
          onClick={() => setIsCampaignFailedModalOpen(false)}
          style={{ marginTop: 8 }}>
          Close
        </Button>
      </Modal>
      <Modal
        open={isModalWarningCreateCampaignVisible}
        onCancel={() => setIsModalWarningCreateCampaignVisible(false)}
        footer={null}
        centered
        width={620}
        closeIcon={<CloseOutlined />}
        styles={{ body: { paddingTop: 28, paddingBottom: 16 } }}>
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%" }}
          size={16}>
          <ExclamationCircleFilled style={{ fontSize: 80, color: "#EEB711" }} />
          <Title level={4} style={{ margin: 0, textAlign: "center" }}>
            Send with Partial Reach
          </Title>
          <Text type="secondary" style={{ textAlign: "center" }}>
            Your budget will only cover <b>{maxReach}</b> of{" "}
            <b>{estimatedTotalMember}</b> targeted members. Do you want to
            continue?
          </Text>

          <Space style={{ marginTop: 12 }}>
            <Button
              size="large"
              onClick={() => setIsModalWarningCreateCampaignVisible(false)}>
              Go Back
            </Button>
            <Button
              type="primary"
              size="large"
              // onClick={() => handleSaveCampaign()}
            >
              Send Anyway
            </Button>
          </Space>
        </Space>
      </Modal>
      <RFMGeneratingModal
        visible={loading || loadingRefreshMember}
        message="Preparing Campaign Data..."
      />
      <CustomModal
        type="timeout"
        title="Editing Unavailable"
        message={
          isBilled
            ? "This campaign can no longer be edited after payment is completed."
            : "The campaign is approaching its scheduled send time, so editing is no longer available."
        }
        isVisible={showTimeout}
        onClose={() => setShowTimeout(false)}
        autoClose={false}
      />
      <MessageTemplatesModal
        campaignType={selectedType}
        open={messageTemplatesOpen}
        templates={listMessageTemplates}
        onCancel={() => setMessageTemplatesOpen(false)}
        onSelect={(template) => {
          setSelectedMessageTemplates(template);
          setMessageTemplatesOpen(false);
        }}
        selectedTemplates={selectedMessageTemplates ?? undefined}
      />
      <PreviewMemberModal
        open={isPreviewOpen}
        onClose={closePreview}
        estimatedMember={estimatedMember}
        campaignType={selectedType}
      />
      <ExcludeMemberPreviewModal
        open={isExcludePreviewOpen}
        onClose={() => setIsExcludePreviewOpen(false)}
        rows={excludeRows}
      />
      <Modal
        open={emailPreviewOpen}
        onCancel={() => {
          setEmailPreviewOpen(false);
          setEmailPreviewHtml(null);
        }}
        footer={null}
        centered
        width={820}
        title="Email Template Preview"
        styles={{ body: { padding: 0, minHeight: 200 } }}>
        {emailPreviewLoading ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: 60,
              gap: 12,
            }}>
            <SyncOutlined spin style={{ fontSize: 32, color: "#1677ff" }} />
            <div style={{ color: "#888" }}>Loading preview...</div>
          </div>
        ) : emailPreviewHtml ? (
          <iframe
            srcDoc={emailPreviewHtml}
            title="Email Preview"
            style={{
              width: "100%",
              height: 640,
              border: "none",
              display: "block",
            }}
          />
        ) : (
          <div style={{ textAlign: "center", padding: 40, color: "#888" }}>
            No preview available.
          </div>
        )}
      </Modal>
      <TargetMemberPreviewModal
        open={isTargetPreviewOpen}
        onClose={() => setIsTargetPreviewOpen(false)}
        rows={targetPreviewRows}
      />
    </Form>
  );
};

export default UpdateCampaign;
